import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

// PrimeReact components
import { useToast } from '../../context/ToastContext';

// Context and services
import { useScenarioContext, scenarioActions, FAVORITES_UI_CACHE_KEY, scenarioKey, ALTERNATE_SCENARIO_ID_BASE } from '../../context/ScenarioContext';
import ApiService, { handleApiError } from '../../services/api';
import { useExportScenarios } from '../../hooks/useExportScenarios';
import { usePracticeData } from '../../hooks/usePracticeData';

// Types
import { ScenarioResult, PayerMixItem, ProductFamily, PayerProfitabilityItem, RebateDetail } from '../../types';

// Styles
import './ScenarioResults.css';
import QtdDistributionChart from '../ScenarioChart/QtdDistributionChart';
import RebateDetailDialog from '../../components/RebateDetailDialog/RebateDetailDialog';
import {
  BreakdownTable,
  Card as UiCard,
  familyLabel,
  Field,
  FilterBand,
  Modal,
  Note,
  PageHeader,
  PayerAssignmentTable,
  PayerEligibility,
  notify,
  useCountUp,
  useSortedPaged,
  Tip,
} from '../../components/ui';
import type { SortColumn } from '../../components/ui';
import CompareModal, { scenarioBadge } from './CompareModal';
import ScenarioTable, { SCENARIO_PAGE_SIZE, type GroupedScenarioResult, type ScenarioSortKey } from './ScenarioTable';
import { AlternateAssignmentTable, AlternateMixTable, AlternatePreviewTable } from './AlternateAssignmentTables';
import { money, shortDate, signedMoney, unitStrength } from '../../utils/format';
import { buildFamilyColors, familyColor, sortByFamily } from '../../utils/families';
import { Button, Dropdown, Menu, type MenuItem, type MenuRef, ProgressBar, StatusTag, TextError, Tooltip } from '@allygpo/ally-ux';


// Reserved scenario_id for the "current mix at the new volume" reference row
// (must match SCALED_CURRENT_SCENARIO_ID in the backend scenario_service).
const SCALED_CURRENT_SCENARIO_ID = 9999;
// Reserved scenario_id for the scenario at exactly the proposed mix from New run
// (must match PROPOSED_SCENARIO_ID in the backend scenario_service).
const PROPOSED_SCENARIO_ID = 9998;


const ScenarioResults: React.FC = () => {
  const { state, dispatch } = useScenarioContext();
  const { isDemoMode } = usePracticeData();
  // The app-wide toast (Layout), so messages survive navigation, e.g. generation finishing after Results opens.
  const toast = useToast();
  const navigate = useNavigate();
  const exportMenuRef = useRef<MenuRef | null>(null);
  const modalExportMenuRef = useRef<MenuRef | null>(null);

  // State
  const { isExporting, handleExport, handleExportWithPayerAssignments } = useExportScenarios({ filenamePrefix: 'scenario_results', toastRef: toast });
  // Nothing is expanded on first load.
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({});
  // Scenarios checked for comparison (Current is always included, so it is never in here).
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  // What the Compare modal shows: the checked set, or the one scenario from the highest-NCR strip.
  const [compareIds, setCompareIds] = useState<number[]>([]);
  const [isComparisonModalVisible, setComparisonModalVisible] = useState(false);
  const [isDecisionLogicModalVisible, setDecisionLogicModalVisible] = useState(false);
  const [decisionLogicScenario, setDecisionLogicScenario] = useState<GroupedScenarioResult | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  // Toggle between viewing Medicare reimbursement + NCR vs Weighted reimbursement + Weighted NCR
  const [ncrMode, setNcrMode] = useState<'medicare' | 'weighted'>('medicare');
  // Toggle between per-category-unit (EU) and per-invoice-unit (IU) figures. Always opens on category.
  const [unitMode, setUnitMode] = useState<'category' | 'invoice'>('category');
  const [generationBannerDismissed, setGenerationBannerDismissed] = useState(false);
  const [excludedPayerBannerDismissed, setExcludedPayerBannerDismissed] = useState(false);
  const [favorites, setFavoritesState] = useState<Set<string>>(() => {
    // Initialize from localStorage cache - this is just UI state for persistence
    try {
      const cached = localStorage.getItem(FAVORITES_UI_CACHE_KEY);
      if (cached) {
        const parsedCache = new Set<string>(JSON.parse(cached) as string[]);
        return parsedCache;
      }
    } catch (error) {
      console.error('Failed to load favorites from cache');
    }
    return new Set<string>();
  });
  // Mirrors `favorites` synchronously, so a toast's Undo (which runs after later renders) and
  // quick repeat clicks act on the latest set rather than the one captured when they were made.
  const favoritesRef = useRef(favorites);
  const setFavorites = (next: Set<string>) => {
    favoritesRef.current = next;
    setFavoritesState(next);
  };
  // Payer mix stored per category; payerMix and payerMixAvailable are derived from selectedCategory
  const [payerMixByCategory, setPayerMixByCategory] = useState<Record<string, PayerMixItem[]>>({});
  const fetchedCategoryMixRef = useRef<Set<string>>(new Set());

  // Drug ↔ payer eligibility (payer preferences)
  const [drugPayerMatrix, setDrugPayerMatrix] = useState<Record<string, Record<string, boolean>>>({});
  const [payerPrefsLoading, setPayerPrefsLoading] = useState(false);
  const [payerPrefsError, setPayerPrefsError] = useState<string | null>(null);

  // Product family list for the category (used for alternate scenario dropdown options)
  const [productFamiliesByCategory, setProductFamiliesByCategory] = useState<Record<string, ProductFamily[]>>({});
  const [productFamiliesLoading, setProductFamiliesLoading] = useState(false);
  const [productFamiliesError, setProductFamiliesError] = useState<string | null>(null);

  // Expanded payer rows inside Decision logic (per scenario_id)
  const [expandedDecisionPayers, setExpandedDecisionPayers] = useState<Record<number, Record<string, boolean>>>({});

  // Per-payer profitability (fee-schedule reimbursement based) — computed inline by the
  // generation endpoints and persisted in context + localStorage like results/assignments.
  const profitabilityByScenario = state.payerProfitabilityByScenario ?? {};

  // Rebate drill-down rows. Persisted trimmed and capped, so they survive a refresh.
  // Memoized so the `?? []` fallback doesn't hand the hooks below a fresh array each render.
  const rebateDetails = useMemo(() => state.rebateDetails ?? [], [state.rebateDetails]);
  const [rebateDetailTarget, setRebateDetailTarget] = useState<{ scenarioId: number; pfId: string; pfLabel: string } | null>(null);
  // Separate from the target so closing can animate out with its content still mounted.
  const [rebateDetailVisible, setRebateDetailVisible] = useState(false);
  const rebateDetailRows = useMemo(() => {
    if (!rebateDetailTarget) return [];
    return rebateDetails.filter(d =>
      Number(d.scenario_id) === rebateDetailTarget.scenarioId &&
      String(d.product_family_id) === String(rebateDetailTarget.pfId)
    );
  }, [rebateDetails, rebateDetailTarget]);

  // Scoped to the result rows an export carries, so the sheet matches the one beside it.
  const rebateDetailsForExport = useCallback((data: ScenarioResult[]): RebateDetail[] => {
    if (rebateDetails.length === 0 || data.length === 0) return [];
    const wanted = new Set(
      data.map(r => `${Number(r.scenario_id)}:${String(r.product_family_id)}`)
    );
    return rebateDetails.filter(d =>
      wanted.has(`${Number(d.scenario_id)}:${String(d.product_family_id)}`)
    );
  }, [rebateDetails]);


  // Presence decides whether the drill-down link renders; the flag picks the tooltip wording.
  // Families with no contract still come back from the SP as placeholder rows, so only a real
  // contract row or a manual adjustment counts.
  const pfRebateEarning = useMemo(() => {
    const byPf = new Map<string, boolean>();
    rebateDetails.forEach(d => {
      if (!d.has_contract && d.rebate_source !== 'Excluded') return;
      const key = `${Number(d.scenario_id)}:${String(d.product_family_id)}`;
      byPf.set(key, (byPf.get(key) ?? false) || Number(d.rebate_earned || 0) > 0);
    });
    return byPf;
  }, [rebateDetails]);

  // Alternate scenario mode (Decision logic modal)
  const [alternateScenarioId, setAlternateScenarioId] = useState<number | null>(null);
  const [alternateModeEnabled, setAlternateModeEnabled] = useState(false);
  const [alternatePayerAssignments, setAlternatePayerAssignments] = useState<Record<string, string>>({});
  const [isCalculatingAlternate, setIsCalculatingAlternate] = useState(false);
  const [alternatePreviewExpanded, setAlternatePreviewExpanded] = useState(false);
  // Pinned ordering for user-run alternate scenarios — stored in context so it survives navigation
  // Memoized: a fresh `[]` each render would re-run every memo that depends on it.
  const pinnedAlternateScenarioIds = useMemo(() => state.pinnedAlternateScenarioIds ?? [], [state.pinnedAlternateScenarioIds]);
  const setPinnedAlternateScenarioIds = (updater: number[] | ((prev: number[]) => number[])) => {
    const next = typeof updater === 'function' ? updater(pinnedAlternateScenarioIds) : updater;
    dispatch(scenarioActions.setPinnedAlternateScenarioIds(next));
  };
  // New ids for an alternate response: 0 (Current) stays 0, every other id gets the next
  // alternate id. Alternate ids sit above every generated id and are never reused, so a pin
  // can't match a scenario in another area.
  const allocateAlternateIds = (responseIds: number[]): Record<number, number> => {
    const map: Record<number, number> = {};
    let nextId = Math.max(ALTERNATE_SCENARIO_ID_BASE, ...pinnedAlternateScenarioIds.map(id => Number(id) + 1));
    Array.from(new Set(responseIds)).forEach((sid) => { map[sid] = sid === 0 ? 0 : nextId++; });
    return map;
  };

  // Legacy alternate scenario modal (distribution-based, no payer assignments)
  const [isLegacyAlternateModalVisible, setLegacyAlternateModalVisible] = useState(false);
  const [legacyAlternateScenario, setLegacyAlternateScenario] = useState<GroupedScenarioResult | null>(null);
  const [legacyAlternateDistributions, setLegacyAlternateDistributions] = useState<Record<string, number>>({});
  const [isCalculatingLegacyAlternate, setIsCalculatingLegacyAlternate] = useState(false);

  // Derived from selectedCategory — updates automatically when the category pill changes
  const payerMix = useMemo(
    () => (selectedCategory ? (payerMixByCategory[selectedCategory] ?? []) : []),
    [payerMixByCategory, selectedCategory]
  );
  const payerMixAvailable = payerMix.length > 0;
  // True once the payer-mix fetch for the selected category has resolved (any
  // outcome). The Units filter is gated on this so it appears together with the
  // async Metric filter instead of rendering ahead of it. When there's no
  // category to fetch, treat it as loaded so the Units filter still shows.
  const payerMixLoaded = !selectedCategory || (selectedCategory in payerMixByCategory);

  // UI-only alternate numbering (scenario_id stays an int everywhere)
  const altIndexByScenarioId = useMemo(() => {
    const map = new Map<number, number>();
    pinnedAlternateScenarioIds.forEach((sid, idx) => map.set(Number(sid), idx + 1));
    return map;
  }, [pinnedAlternateScenarioIds]);

  // Resolve the modeled category-size delta (%) for the scaled-current row.
  // Prefer the factor the user set on the generator; if that isn't available
  // (e.g. after a reload where the generator form state was cleared), derive it
  // from the data: scaled-current units vs current units for the selected category.
  const getScaledCurrentPct = (): number => {
    const mapFactor = state.generatorState?.form?.category_scale_factor_map?.[selectedCategory ?? ''];
    if (mapFactor != null && Math.abs(mapFactor - 1) > 1e-9) {
      return Math.round((mapFactor - 1) * 100);
    }
    const inCat = (r: ScenarioResult) => !selectedCategory || r.category === selectedCategory;
    const sumUnits = (sid: number) =>
      state.scenarioResults
        .filter((r) => Number(r.scenario_id) === sid && inCat(r))
        .reduce((s, r) => s + Number(r.category_units || 0), 0);
    const base = sumUnits(0);
    const scaled = sumUnits(SCALED_CURRENT_SCENARIO_ID);
    return base > 0 && scaled > 0 ? Math.round((scaled / base - 1) * 100) : 0;
  };

  const getScenarioDisplayLabel = (scenarioId: number) => {
    if (scenarioId === 0) return 'Current';
    // Reserved id for the "current mix at the new volume" reference (backend
    // SCALED_CURRENT_SCENARIO_ID). Always show the modeled delta, e.g. "Current @ +50%".
    // The backend only injects this row when the factor is off baseline, so pct is non-zero.
    if (scenarioId === SCALED_CURRENT_SCENARIO_ID) {
      const pct = getScaledCurrentPct();
      return `Current @ ${pct > 0 ? '+' : ''}${pct}%`;
    }
    // Reserved id for the scenario at exactly the proposed mix (backend PROPOSED_SCENARIO_ID).
    if (scenarioId === PROPOSED_SCENARIO_ID) return 'Proposed';
    const altIdx = altIndexByScenarioId.get(scenarioId);
    return altIdx != null ? `Alt ${altIdx}` : `Scenario ${scenarioId}`;
  };

  // Sync state.currentUser with the account number from the actual results data.
  // The results carry `logged_in_user` (the account number used for generation),
  // which must match what we send in API calls (favorites, payer mix, etc.).
  useEffect(() => {
    if (state.scenarioResults.length > 0) {
      const resultsAccountNumber = state.scenarioResults[0]?.logged_in_user;
      if (resultsAccountNumber && resultsAccountNumber !== state.currentUser) {
        dispatch(scenarioActions.setCurrentUser(resultsAccountNumber));
      }
      // Reset banner dismiss flags on new generation
      setGenerationBannerDismissed(false);
      setExcludedPayerBannerDismissed(false);
    }
  // Re-sync only when new results arrive; a practice change in the top bar must not be undone here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.scenarioResults, dispatch]);

  // Clear per-category payer mix cache whenever the logged-in user changes
  useEffect(() => {
    fetchedCategoryMixRef.current = new Set();
    setPayerMixByCategory({});
  }, [state.currentUser]);

  // Lazily fetch payer mix for the selected category and cache it for the session
  useEffect(() => {
    if (!state.currentUser || !selectedCategory) return;
    const cacheKey = `${state.currentUser}:${selectedCategory}`;
    if (fetchedCategoryMixRef.current.has(cacheKey)) return;
    fetchedCategoryMixRef.current.add(cacheKey);

    let cancelled = false;
    const doFetch = async () => {
      try {
        const resp = await ApiService.getPayerMixByCategory({ logged_in_user: state.currentUser!, category: selectedCategory });
        const mix = ((resp as any)?.mix ?? []) as PayerMixItem[];
        if (!cancelled) {
          setPayerMixByCategory(prev => ({ ...prev, [selectedCategory]: mix }));
        }
      } catch {
        if (!cancelled) {
          setPayerMixByCategory(prev => ({ ...prev, [selectedCategory]: [] }));
          fetchedCategoryMixRef.current.delete(cacheKey);
        }
      }
    };
    doFetch();
    return () => { cancelled = true; };
  }, [state.currentUser, selectedCategory]);

  // Fetch payer prefs for the selected category (same pattern as Generator page)
  useEffect(() => {
    const userId = state.currentUser;
    const category = selectedCategory;
    if (!userId || !category) {
      setDrugPayerMatrix({});
      setPayerPrefsLoading(false);
      setPayerPrefsError(null);
      return;
    }

    let cancelled = false;
    const run = async () => {
      setPayerPrefsLoading(true);
      setPayerPrefsError(null);
      try {
        const resp = await ApiService.getDrugPayerMatrix(userId, category);
        const matrix = (resp as any)?.drugPayerMatrix || (resp as any)?.preferences || {};
        if (!cancelled) setDrugPayerMatrix(matrix);
      } catch (e) {
        if (!cancelled) {
          setDrugPayerMatrix({});
          setPayerPrefsError(handleApiError(e));
        }
      } finally {
        if (!cancelled) setPayerPrefsLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [state.currentUser, selectedCategory]);

  // Fetch all product families for the selected category (names-all), to populate alternate dropdown options.
  useEffect(() => {
    const userId = state.currentUser;
    const category = selectedCategory;
    if (!userId || !category) {
      setProductFamiliesLoading(false);
      setProductFamiliesError(null);
      return;
    }

    let cancelled = false;
    const run = async () => {
      // Avoid refetch if we already have it
      if (productFamiliesByCategory[category]?.length) return;

      setProductFamiliesLoading(true);
      setProductFamiliesError(null);
      try {
        const resp = await ApiService.getCategoryProductFamiliesNames(userId, category);
        const families = (resp as any)?.data?.product_families ?? (resp as any)?.product_families ?? [];
        if (!cancelled) {
          setProductFamiliesByCategory((prev) => ({ ...prev, [category]: families as ProductFamily[] }));
        }
      } catch (e) {
        if (!cancelled) {
          setProductFamiliesError(handleApiError(e));
        }
      } finally {
        if (!cancelled) setProductFamiliesLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentUser, selectedCategory]);

  // Per-payer profitability is computed server-side and returned with the generation
  // response (ScenarioResultsResponse.profitability), then merged into context by the
  // generator (batch) and the alternate recalc below. No separate fetch is needed here.

  // No client-side recalculation; use backend-provided fields only.

  const categoryOptions = Array.from(new Set(state.scenarioResults.map(r => r.category))).map(cat => ({
    label: cat,
    value: cat
  }));

  // Generate a unique signature for a complete scenario (all product families together)
  const getScenarioSignature = (scenarioDetails: ScenarioResult[]) => {
    // Sort by product_family_id to ensure consistent ordering
    const sorted = [...scenarioDetails].sort((a, b) => 
      String(a.product_family_id).localeCompare(String(b.product_family_id))
    );
    // Create a signature from the distribution and invoice units of each product family
    const signature = {
      logged_in_user: sorted[0]?.logged_in_user,
      category: sorted[0]?.category,
      distributions: sorted.map(detail => ({
        product_family_id: detail.product_family_id,
        future_distribution: detail.future_distribution,
        invoice_units: detail.invoice_units
      }))
    };
    return JSON.stringify(signature);
  };

  // Clear favorites cache only when new scenarios are actually generated
  useEffect(() => {
    // Only clear cache when generation just finished (transitioned from true to false)
    const wasGenerating = sessionStorage.getItem('was_generating') === 'true';
    const isGenerating = state.generationStatus?.inProgress === true;
    const completedCategories = state.generationStatus?.completedCategories ?? 0;
    
    if (wasGenerating && !isGenerating && completedCategories > 0) {
      try {
        localStorage.removeItem(FAVORITES_UI_CACHE_KEY);
        setFavorites(new Set());
      } catch (error) {
        console.error('Failed to clear favorites cache');
      }
    }
    
    // Track generation state
    sessionStorage.setItem('was_generating', isGenerating ? 'true' : 'false');
  }, [state.generationStatus?.inProgress, state.generationStatus?.completedCategories]);

  const toggleFavorite = async (scenario: GroupedScenarioResult) => {
    if (scenario.details.length === 0) return;

    // Demo accounts are read-only server-side (423). Stop here rather than
    // letting the optimistic update flip the star and then roll back.
    if (isDemoMode) {
      toast.current?.show({
        severity: 'info',
        summary: 'Read-only demo',
        detail: 'Favorites cannot be saved from a demo account.',
        life: 3000,
      });
      return;
    }
    
    // Generate signature for the complete scenario (all product families)
    const signature = getScenarioSignature(scenario.details);
    const isFavorite = favoritesRef.current.has(signature);
    const label = getScenarioDisplayLabel(Number(scenario.scenario_id));

    // Create new set with the change applied
    const newFavorites = new Set(favoritesRef.current);

    if (isFavorite) {
      // Removing favorite - remove the scenario signature
      newFavorites.delete(signature);
      // Update UI immediately for responsiveness
      setFavorites(newFavorites);
      
      // Update localStorage cache immediately for UI persistence
      try {
        localStorage.setItem(FAVORITES_UI_CACHE_KEY, JSON.stringify(Array.from(newFavorites)));
      } catch (error) {
        console.error('Failed to update favorites cache');
      }
      
      try {
        // Remove updated_by if present to prevent API issues
        const resultsToUpdate = scenario.details.map(d => {
          const { updated_by, ...rest } = d as ScenarioResult & { updated_by?: unknown };
          return { ...rest, is_active: false };
        });
        
        await ApiService.updateFavoriteScenario({
          user_id: state.currentUser || '',
          category: selectedCategory || scenario.details[0].category,
          scenario_results: resultsToUpdate
        });
        
        notify(toast, `${label} removed from favorites`);
      } catch (error) {
        console.error('Failed to remove favorite:', error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to remove favorite scenario',
          life: 3000,
        });
        // Revert by adding it back
        const revertedFavorites = new Set(favoritesRef.current);
        revertedFavorites.add(signature);
        setFavorites(revertedFavorites);
        // Update localStorage cache to match reverted state
        try {
          localStorage.setItem(FAVORITES_UI_CACHE_KEY, JSON.stringify(Array.from(revertedFavorites)));
        } catch (error) {
          console.error('Failed to update favorites cache');
        }
      }
    } else {
      // Adding favorite - add the scenario signature
      newFavorites.add(signature);
      // Update UI immediately for responsiveness
      setFavorites(newFavorites);
      
      // Update localStorage cache immediately for UI persistence
      try {
        localStorage.setItem(FAVORITES_UI_CACHE_KEY, JSON.stringify(Array.from(newFavorites)));
      } catch (error) {
        console.error('Failed to update favorites cache');
      }
      
       try {
         const payerAssignmentsToPersist =
           scenario.payerAssignments && Object.keys(scenario.payerAssignments).length > 0
             ? scenario.payerAssignments
             : undefined;

         // Remove updated_by if present to prevent API issues
         const resultsToSave = scenario.details.map(d => {
           const { updated_by, ...rest } = d as ScenarioResult & { updated_by?: unknown };
           return {
             ...rest,
             is_active: true,
             ...(payerAssignmentsToPersist ? { payer_assignments: payerAssignmentsToPersist } : {}),
           };
         });
         
         // Persist this scenario's drill-down rows (including no-contract placeholders,
         // which let Favorites tell "no contracts" apart from "nothing saved") so the
         // rebate detail dialog works there without regenerating.
         const rebateDetailsToPersist = rebateDetails.filter(
           d => Number(d.scenario_id) === Number(scenario.scenario_id)
         );

         await ApiService.saveFavoriteScenario({
           user_id: state.currentUser || '',
           category: selectedCategory || scenario.details[0].category,
           scenario_results: resultsToSave,
           ...(payerAssignmentsToPersist ? { payer_assignments: payerAssignmentsToPersist } : {}),
           ...(rebateDetailsToPersist.length > 0 ? { rebate_details: rebateDetailsToPersist } : {}),
         });
         notify(toast, `${label} added to favorites`);
      } catch (error) {
        console.error('Failed to save favorite:', error);
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save favorite scenario',
          life: 3000,
        });
        // Revert by removing it
        const revertedFavorites = new Set(favoritesRef.current);
        revertedFavorites.delete(signature);
        setFavorites(revertedFavorites);
        // Update localStorage cache to match reverted state
        try {
          localStorage.setItem(FAVORITES_UI_CACHE_KEY, JSON.stringify(Array.from(revertedFavorites)));
        } catch (error) {
          console.error('Failed to update favorites cache');
        }
      }
    }
  };

  const isFavorite = (scenario: GroupedScenarioResult) =>
    scenario.details.length > 0 && favorites.has(getScenarioSignature(scenario.details));

  // When switching categories, automatically collapse any expanded scenario detail rows
  useEffect(() => {
    let changed = false;
    if (Object.keys(expandedRows).length > 0) {
      setExpandedRows({});
      changed = true;
    }
    // Clear comparison selections to avoid mixing scenarios across categories
    if (selectedIds.length > 0) {
      setSelectedIds([]);
      changed = true;
    }
    if (changed) {
      // Optionally could toast or log; keeping silent for UX simplicity
    }
  // Resets on an area change only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  // When category options change, ensure a valid category is selected.
  useEffect(() => {
    const categoryValues = categoryOptions.map(c => c.value);
    // If there's no selected category or the selected one is no longer valid,
    // and there are available categories, select the first one.
    if ((!selectedCategory || !categoryValues.includes(selectedCategory)) && categoryValues.length > 0) {
      setSelectedCategory(categoryValues[0]);
    } else if (categoryValues.length === 0 && selectedCategory) {
      // No results (e.g. after a practice switch): drop the old area so the page doesn't fetch
      // the new practice's payer data for an area that belonged to the previous one.
      setSelectedCategory(null);
    }
  }, [categoryOptions, selectedCategory]);

  // Sync locally selected category to global context state.currentCategory
  useEffect(() => {
    if (selectedCategory) {
      dispatch(scenarioActions.setCurrentCategory(selectedCategory));
    }
  }, [selectedCategory, dispatch]);

  const sortScenarioIdsWithPinnedAlternates = useCallback((scenarioIdA: number, scenarioIdB: number) => {
    const idxA = pinnedAlternateScenarioIds.indexOf(scenarioIdA);
    const idxB = pinnedAlternateScenarioIds.indexOf(scenarioIdB);
    const inPinnedA = idxA >= 0;
    const inPinnedB = idxB >= 0;

    if (inPinnedA && inPinnedB) return idxB - idxA;
    if (inPinnedA && !inPinnedB) return -1;
    if (!inPinnedA && inPinnedB) return 1;

    // Current (scaled) sits immediately after the baseline (Current) and before
    // the modeled scenarios, despite its large reserved id.
    const rank = (id: number) => (id === SCALED_CURRENT_SCENARIO_ID ? 0.5 : id);
    return rank(scenarioIdA) - rank(scenarioIdB);
  }, [pinnedAlternateScenarioIds]);

  // Neither the modelling SP nor the grouping below imposes an order on product families, and
  // SQL Server guarantees none without an ORDER BY — so rows could come back in a different
  // order between runs, which makes scenarios hard to compare and reads as if the data changed.
  // Ordering here rather than at each consumer keeps the table, the expanded breakdown and both
  // export paths (all results / selected scenarios) in agreement.
  const orderedScenarioResults = useMemo(() => (
    [...state.scenarioResults].sort((a, b) =>
      sortScenarioIdsWithPinnedAlternates(Number(a.scenario_id), Number(b.scenario_id)) ||
      (a.product_family_name ?? '').localeCompare(b.product_family_name ?? '', undefined, { sensitivity: 'base' }))
  ), [state.scenarioResults, sortScenarioIdsWithPinnedAlternates]);

  // Data restricted to selected category for exports and other category-specific operations
  const selectedCategoryResults = useMemo(() => {
    return selectedCategory
      ? orderedScenarioResults.filter(r => r.category === selectedCategory)
      : [];
  }, [orderedScenarioResults, selectedCategory]);

  // True when every product family in the selected category is missing a fee
  // schedule — weighted NCR would just repeat Medicare NCR, so the toggle is
  // disabled. Stays false until results have loaded (so the button is enabled
  // by default and only disabled once we can confirm the full set is missing).
  const allFeeSchedulesMissing = useMemo(() => {
    if (selectedCategoryResults.length === 0) return false;
    const missingSet = new Set(
      (state.missingFeeSchedulePfIds?.[selectedCategory ?? ''] ?? []).map(String)
    );
    return selectedCategoryResults.every(r => missingSet.has(String(r.product_family_id)));
  }, [selectedCategoryResults, state.missingFeeSchedulePfIds, selectedCategory]);

  // Weighted NCR is not applicable for oral inventory categories.
  const categoryIsOral = useMemo(() => {
    if (!selectedCategory) return false;
    return (productFamiliesByCategory[selectedCategory] ?? []).some(
      pf => (pf.inventory_type ?? '').trim().toLowerCase() === 'oral'
    );
  }, [selectedCategory, productFamiliesByCategory]);

  // Oral categories use benchmark (not Medicare) reimbursement terminology.
  const ncrLabel = categoryIsOral ? 'Benchmark' : 'Medicare';

  // Invoice unit strength (default NDC vial strength + UOM, from DisplayUnitEquivalency), shown
  // beside each family when units are invoice units. Oral categories don't use it.
  const showStrength = unitMode === 'invoice' && !categoryIsOral;
  const strengthByFamily = useMemo(() => {
    const map = new Map<string, string>();
    Object.entries(productFamiliesByCategory).forEach(([category, families]) => {
      families.forEach((pf) => {
        const strength = unitStrength(pf.display_unit_equivalency);
        if (!strength) return;
        map.set(`${category}|id:${pf.product_family_id}`, strength);
        map.set(`${category}|name:${(pf.product_family_name ?? '').trim().toLowerCase()}`, strength);
      });
    });
    return map;
  }, [productFamiliesByCategory]);
  const strengthOf = useCallback((category: string | null | undefined, pfId?: string | number | null, name?: string | null) => (
    strengthByFamily.get(`${category ?? ''}|id:${pfId ?? ''}`)
      ?? strengthByFamily.get(`${category ?? ''}|name:${(name ?? '').trim().toLowerCase()}`)
  ), [strengthByFamily]);

  // Ensure weighted mode is only available when payer mix is present
  useEffect(() => {
    if ((!payerMixAvailable || allFeeSchedulesMissing || categoryIsOral) && ncrMode === 'weighted') {
      setNcrMode('medicare');
    }
  }, [payerMixAvailable, allFeeSchedulesMissing, categoryIsOral, ncrMode]);

  // One colour per family, from the shared palette by quarter-to-date share rank, so the QTD
  // chart, legends, mix bars and breakdowns agree. Families only present in the results (e.g.
  // before the category list loads) still get a colour, after the ranked ones.
  const familyColors = useMemo(() => {
    const listed = (productFamiliesByCategory[selectedCategory ?? ''] ?? []).map(pf => ({
      name: pf.product_family_name || '',
      share: pf.product_family_to_category_qtd_ratio ?? 0,
    }));
    const known = new Set(listed.map(f => f.name.trim().toLowerCase()));
    const extra = Array.from(new Set(state.scenarioResults
      .filter(r => !selectedCategory || r.category === selectedCategory)
      .map(r => r.product_family_name || '')
      .filter(n => n && !known.has(n.trim().toLowerCase()))))
      .map(name => ({ name, share: null }));
    return buildFamilyColors([...listed, ...extra]);
  }, [productFamiliesByCategory, selectedCategory, state.scenarioResults]);

  const getColorForFamily = useCallback((familyName: string): string => familyColor(familyColors, familyName), [familyColors]);


  const getDistributionBreakdown = (details: ScenarioResult[]) => {
    const distributionMap: Record<string, number> = {};

    for (const row of details) {
      const family = row.product_family_name || 'Other';
      distributionMap[family] = (distributionMap[family] || 0) + (row.future_distribution ?? 0);
    } 

    const total = Object.values(distributionMap).reduce((a, b) => a + b, 0) || 1;

    // Fixed family order, so mix bars read the same way from one scenario to the next.
    return sortByFamily(Object.entries(distributionMap), ([family]) => family).map(([family, value]) => ({
      family,
      percent: value / total,
      color: getColorForFamily(family)
    }));
  };

  const formatPercentage = (value: number): string => {
    return Math.round(value * 100) + '%';
  };

  const normalizePayerName = (name: string) => name.trim().toLowerCase().replace(/\s+/g, ' ');

  const drugPayerMatrixByNormalizedPayer = useMemo(() => {
    const out: Record<string, Record<string, boolean>> = {};
    Object.entries(drugPayerMatrix || {}).forEach(([productFamilyId, row]) => {
      const normalizedRow: Record<string, boolean> = {};
      Object.entries(row || {}).forEach(([payer, allowed]) => {
        normalizedRow[normalizePayerName(String(payer))] = (allowed as any) === true;
      });
      out[String(productFamilyId)] = normalizedRow;
    });
    return out;
  }, [drugPayerMatrix]);

  const computeDistributionFromAssignments = (params: {
    assignments: Record<string, string>;
    candidateProductFamilyIds: string[];
    payerItems: Array<{ payer: string; vol: number }>;
    baselineMix: Record<string, number>;
    minDistributionMap?: Record<string, number>;
  }): { distributionByProductFamilyId: Record<string, number>; totalAllocatedPercent: number } => {
    const {
      assignments,
      candidateProductFamilyIds,
      payerItems,
      baselineMix,
      minDistributionMap,
    } = params;

    // Backend-equivalent logic (build_distribution_from_assignments)
    // - payerItems.vol is expected to be on a 0..1 scale
    const pfVolume: Record<string, number> = {};
    candidateProductFamilyIds.forEach((pfId) => {
      pfVolume[String(pfId)] = 0;
    });

    // Normalize assignment keys to tolerate casing/spacing mismatches.
    const assignmentByNormalizedPayer: Record<string, string> = {};
    Object.entries(assignments || {}).forEach(([payer, pfId]) => {
      assignmentByNormalizedPayer[normalizePayerName(String(payer))] = String(pfId);
    });

    // Step 1: Add minimums first
    const minMap = minDistributionMap ?? {};
    candidateProductFamilyIds.forEach((pfIdRaw) => {
      const pfId = String(pfIdRaw);
      pfVolume[pfId] = (pfVolume[pfId] || 0) + Number(minMap[pfId] ?? 0);
    });

    // Step 2: Add assigned payer volumes
    let assignedTotal = 0;
    payerItems.forEach((p) => {
      const payerNorm = normalizePayerName(String(p.payer));
      const vol = Number(p.vol ?? 0);
      if (!vol || Number.isNaN(vol)) return;
      const assignedPfId = assignmentByNormalizedPayer[payerNorm];
      if (!assignedPfId) return;
      if (pfVolume[assignedPfId] === undefined) pfVolume[assignedPfId] = 0;
      pfVolume[assignedPfId] += vol;
      assignedTotal += vol;
    });

    // Step 3: Fill remaining flexible volume using baseline fractions
    const sumSoFar = candidateProductFamilyIds.reduce((sum, pfIdRaw) => sum + Number(pfVolume[String(pfIdRaw)] ?? 0), 0);
    const flexibleRemaining = Math.max(0, 1 - sumSoFar);

    const baselineFlexibleSum = candidateProductFamilyIds.reduce((sum, pfIdRaw) => {
      const pfId = String(pfIdRaw);
      return sum + Number(baselineMix?.[pfId] ?? 0);
    }, 0);

    if (baselineFlexibleSum > 0 && flexibleRemaining > 0) {
      candidateProductFamilyIds.forEach((pfIdRaw) => {
        const pfId = String(pfIdRaw);
        const baselineFraction = Number(baselineMix?.[pfId] ?? 0) / baselineFlexibleSum;
        pfVolume[pfId] += baselineFraction * flexibleRemaining;
      });
    }

    // Step 4: Normalize to sum 1
    const total = candidateProductFamilyIds.reduce((sum, pfIdRaw) => sum + Number(pfVolume[String(pfIdRaw)] ?? 0), 0);
    let normalized: Record<string, number> = { ...pfVolume };
    if (total > 0) {
      normalized = candidateProductFamilyIds.reduce<Record<string, number>>((acc, pfIdRaw) => {
        const pfId = String(pfIdRaw);
        const vol = Number(pfVolume[pfId] ?? 0);
        acc[pfId] = Math.round((vol / total) * 1e6) / 1e6;
        return acc;
      }, {});
    }

    return { distributionByProductFamilyId: normalized, totalAllocatedPercent: Math.round(assignedTotal * 1000) / 10 };
  };

  // Round each share to a whole percent and absorb the residual on the largest share, mirroring the
  // backend generator; independent per-item rounding of shares summing to 1.0 otherwise shows a phantom 101%.
  const roundDistributionToWholePercents = (
    dist: Record<string, number>
  ): Record<string, number> => {
    const entries = Object.entries(dist).map(
      ([k, v]) => [k, Math.round((Number(v) || 0) * 100) / 100] as [string, number]
    );
    if (entries.length === 0) return {};
    const sum = entries.reduce((s, [, v]) => s + v, 0);
    const residual = Math.round((1 - sum) * 100) / 100;
    if (residual !== 0) {
      let maxIdx = 0;
      for (let i = 1; i < entries.length; i++) {
        if (entries[i][1] > entries[maxIdx][1]) maxIdx = i;
      }
      entries[maxIdx][1] = Math.round((entries[maxIdx][1] + residual) * 100) / 100;
    }
    return entries.reduce<Record<string, number>>((acc, [k, v]) => {
      acc[k] = v;
      return acc;
    }, {});
  };

  const buildFullDrugPayerMatrixForAlternateScenario = (
    productFamilyIds: string[],
    payerList: PayerMixItem[],
    baseMatrix: Record<string, Record<string, boolean>>,
    payerAssignments: Record<string, string>,
    excludedPayersNormalized: Set<string> = new Set()
  ): Record<string, Record<string, boolean>> => {
    // Normalize assignment keys to tolerate casing/spacing mismatches
    const assignmentByNormalizedPayer: Record<string, string> = {};
    Object.entries(payerAssignments || {}).forEach(([payer, pfId]) => {
      assignmentByNormalizedPayer[normalizePayerName(payer)] = String(pfId);
    });

    const matrix: Record<string, Record<string, boolean>> = {};
    productFamilyIds.forEach((drugId) => {
      const baseRow = baseMatrix?.[drugId] ?? {};
      const baseRowByNormalizedPayer: Record<string, boolean> = {};
      Object.entries(baseRow || {}).forEach(([payerKey, allowed]) => {
        baseRowByNormalizedPayer[normalizePayerName(String(payerKey))] = (allowed as any) === true;
      });
      const row: Record<string, boolean> = {};

      payerList.forEach((p) => {
        const payer = String(p.payer);
        const normalized = normalizePayerName(payer);
        // Payers fully deselected at generation stay excluded across ALL product
        // families — including any introduced in the alternate — so their volume
        // never re-enters the category.
        if (excludedPayersNormalized.has(normalized)) {
          row[payer] = false;
          return;
        }
        const assignedPfId = assignmentByNormalizedPayer[normalized];
        if (assignedPfId) {
          row[payer] = assignedPfId === String(drugId);
        } else {
          // Default to base matrix value, falling back to eligible (true)
          row[payer] = (baseRow[payer] ?? baseRowByNormalizedPayer[normalized] ?? true) === true;
        }
      });

      matrix[String(drugId)] = row;
    });

    return matrix;
  };

  const payerMixByName = useMemo(() => {
    const map: Record<string, number> = {};
    for (const item of payerMix) {
      if (!item?.payer) continue;
      map[normalizePayerName(item.payer)] = Number(item.percent ?? 0);
    }
    return map;
  }, [payerMix]);

  const payerAssignmentsToRows = (
    payerAssignments: Record<string, string> | undefined,
    productFamilyNameById: Record<string, string>,
    payerMixLookupByName: Record<string, number>
  ) => {
    if (!payerAssignments) {
      return [] as Array<{ payer: string; payerPercent?: number; productFamilyId: string; productFamilyName: string }>;
    }

    return Object.entries(payerAssignments)
      .map(([payer, assignedId]) => {
        const productFamilyId = String(assignedId);
        const productFamilyName = productFamilyNameById[productFamilyId] ?? productFamilyId;
        const payerPercent = payerMixLookupByName[normalizePayerName(payer)];
        return { payer, payerPercent, productFamilyId, productFamilyName };
      })
      .sort((a, b) => {
        const ap = typeof a.payerPercent === 'number' && !Number.isNaN(a.payerPercent) ? a.payerPercent : undefined;
        const bp = typeof b.payerPercent === 'number' && !Number.isNaN(b.payerPercent) ? b.payerPercent : undefined;

        if (ap === undefined && bp !== undefined) return 1;
        if (ap !== undefined && bp === undefined) return -1;
        if (ap !== undefined && bp !== undefined && bp !== ap) return bp - ap;

        return a.payer.localeCompare(b.payer);
      });
  };

  const formatPfLabel = (name: string) => name ? name.charAt(0).toUpperCase() + name.slice(1) : '—';

  const openDecisionLogicModal = (scenario: GroupedScenarioResult, opts?: { startAlternate?: boolean }) => {
    setDecisionLogicScenario(scenario);
    setDecisionLogicModalVisible(true);

    // Initialize alternate scenario state for this modal open
    setAlternateScenarioId(scenario.scenario_id);
    setAlternateModeEnabled(opts?.startAlternate === true);
    setAlternatePayerAssignments({ ...(scenario.payerAssignments ?? {}) });
  };

  const closeDecisionLogicModal = () => setDecisionLogicModalVisible(false);

  // Runs once the dialog has finished animating out. Everything here feeds what the dialog
  // renders, so clearing it on the click itself emptied the dialog before it closed.
  const resetDecisionLogicModal = () => {
    setDecisionLogicScenario(null);
    setAlternateScenarioId(null);
    setAlternateModeEnabled(false);
    setAlternatePayerAssignments({});
    setIsCalculatingAlternate(false);
    setAlternatePreviewExpanded(false);
  };

  // Legacy alternate scenario modal (distribution sliders)
  const openLegacyAlternateModal = (scenario: GroupedScenarioResult) => {
    setLegacyAlternateScenario(scenario);
    // Initialize sliders with the scenario's current distribution values
    const initial: Record<string, number> = {};
    scenario.details.forEach((d) => {
      initial[String(d.product_family_id)] = Number(d.future_distribution ?? 0);
    });
    setLegacyAlternateDistributions(initial);
    setLegacyAlternateModalVisible(true);
  };

  const closeLegacyAlternateModal = () => setLegacyAlternateModalVisible(false);

  // Runs once the modal has finished animating out, so its content doesn't blank mid-close.
  const resetLegacyAlternateModal = () => {
    setLegacyAlternateScenario(null);
    setLegacyAlternateDistributions({});
    setIsCalculatingLegacyAlternate(false);
  };

  const resetLegacyAlternateDistributions = () => {
    if (!legacyAlternateScenario) return;
    const initial: Record<string, number> = {};
    legacyAlternateScenario.details.forEach((d) => {
      initial[String(d.product_family_id)] = Number(d.future_distribution ?? 0);
    });
    setLegacyAlternateDistributions(initial);
  };

  const legacyAlternateDistributionTotal = useMemo(() => {
    return Object.values(legacyAlternateDistributions).reduce((sum, v) => sum + (Number(v) || 0), 0);
  }, [legacyAlternateDistributions]);

  const hasLegacyDistributionChanges = useMemo(() => {
    if (!legacyAlternateScenario) return false;
    const original: Record<string, number> = {};
    legacyAlternateScenario.details.forEach((d) => {
      original[String(d.product_family_id)] = Number(d.future_distribution ?? 0);
    });
    // Compare in whole percents, the editor's own precision: shares like 0.4412 show (and round) as 44%,
    // so comparing finer than that counted an untouched editor as changed and enabled Calculate.
    return Object.keys(legacyAlternateDistributions).some((pfId) => {
      const orig = Math.round((original[pfId] ?? 0) * 100);
      const alt = Math.round((legacyAlternateDistributions[pfId] ?? 0) * 100);
      return orig !== alt;
    });
  }, [legacyAlternateScenario, legacyAlternateDistributions]);

  // Build the admin-fee map (product_family_id -> fee per EU) for an alternate
  // scenario recalculation. Mirrors the generator payload so fees aren't dropped.
  // Source priority: cached generator fees (carried from the generate page),
  // falling back to the SP-returned admin_fee_per_eu (AdminFeePerEU column) on
  // the scenario result rows.
  const buildAlternateProductFamilyFeesMap = (
    productFamilyIds: string[],
    details: ScenarioResult[]
  ): Record<string, number> => {
    const cachedFees = state.generatorState?.form?.product_family_fees_map ?? {};
    const feesFromResults: Record<string, number> = {};
    details.forEach((d) => {
      const fee = d.admin_fee_per_eu;
      if (fee != null && !Number.isNaN(Number(fee))) {
        feesFromResults[String(d.product_family_id)] = Number(fee);
      }
    });
    const map: Record<string, number> = {};
    productFamilyIds.forEach((id) => {
      const key = String(id);
      const fee = cachedFees[key] ?? feesFromResults[key];
      if (fee != null && !Number.isNaN(Number(fee))) {
        map[key] = Math.round(Number(fee) * 100) / 100;
      }
    });
    return map;
  };

  // Same carry-forward as buildAlternateProductFamilyFeesMap, for the direct rebate: cached
  // generator input first, then the SP-returned direct_rebate_per_eu on the result rows (which
  // is what an alternate recalculated from a favorite or a reloaded session has).
  const buildAlternateProductFamilyDirectRebatesMap = (
    productFamilyIds: string[],
    details: ScenarioResult[]
  ): Record<string, number> => {
    const cached = state.generatorState?.form?.product_family_direct_rebates_map ?? {};
    const fromResults: Record<string, number> = {};
    details.forEach((d) => {
      const rebate = d.direct_rebate_per_eu;
      if (rebate != null && !Number.isNaN(Number(rebate))) {
        fromResults[String(d.product_family_id)] = Number(rebate);
      }
    });
    const map: Record<string, number> = {};
    productFamilyIds.forEach((id) => {
      const key = String(id);
      const rebate = cached[key] ?? fromResults[key];
      if (rebate != null && !Number.isNaN(Number(rebate))) {
        map[key] = Math.max(0, Math.round(Number(rebate) * 100) / 100);
      }
    });
    return map;
  };

  const runLegacyAlternateCalculation = async () => {
    if (!state.currentUser || !legacyAlternateScenario) return;

    const details = legacyAlternateScenario.details;
    const category = selectedCategory || details[0]?.category;
    if (!category) return;

    // Validate total is exactly 100%
    const total = legacyAlternateDistributionTotal;
    const totalPctCheck = Math.round(total * 100);
    if (totalPctCheck !== 100) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Invalid distribution',
        detail: `Distributions must sum to exactly 100%. Current total: ${totalPctCheck}%`,
        life: 3500,
      });
      return;
    }

    setIsCalculatingLegacyAlternate(true);
    try {
      // Normalize distributions to sum exactly to 1
      const rawTotal = Object.values(legacyAlternateDistributions).reduce((s, v) => s + (Number(v) || 0), 0);
      const normalizedMap: Record<string, number> = {};
      Object.entries(legacyAlternateDistributions).forEach(([pfId, val]) => {
        normalizedMap[pfId] = rawTotal > 0 ? (Number(val) || 0) / rawTotal : 0;
      });

      // Build excludedScenarios from cached state
      const cachedExcludedOpportunities = state.generatorState?.form?.selectedExcludedOpportunities ?? [];
      const excludedScenarios = cachedExcludedOpportunities
        .filter(opp => opp.category === category)
        .map(opp => ({
          logged_in_user: state.currentUser || '',
          category: opp.category || category,
          product_family_id: Number(opp.product_family_id),
          paid_on: opp.paid_on || '',
          rebate: Number(opp.rebate) ?? 0,
        }));

      const feesMap = buildAlternateProductFamilyFeesMap(Object.keys(normalizedMap), details);
      const directRebatesMap = buildAlternateProductFamilyDirectRebatesMap(Object.keys(normalizedMap), details);

      const payload: any = {
        logged_in_user: state.currentUser,
        category,
        max_scenarios: 1,
        min_distribution_map: normalizedMap,
        product_family_fees_map: Object.keys(feesMap).length > 0 ? feesMap : undefined,
        product_family_direct_rebates_map: Object.keys(directRebatesMap).length > 0 ? directRebatesMap : undefined,
        excludedScenarios,
        // Keep alternate scenarios at the same category size the user modeled on generation.
        category_scale_factor: state.generatorState?.form?.category_scale_factor_map?.[category] ?? 1.0,
        batch_or_alternate: 'alternate',
      };

      const resp = await ApiService.generateAndCalculateScenariosWithMinsAndMaxAndPvt(payload);

      const rawResults = (resp as any)?.results ?? [];
      const rawSummary = (resp as any)?.summary ?? [];

      const scenarioIdMap = allocateAlternateIds((rawResults as any[]).map(r => Number(r.scenario_id)));

      const results = (rawResults as any[]).map((r) => ({
        ...r,
        scenario_id: scenarioIdMap[Number(r.scenario_id)] ?? Number(r.scenario_id),
      }));

      const summaryArray = Array.isArray(rawSummary) ? rawSummary : [rawSummary];
      let summary = (summaryArray as any[]).map((s) => ({
        ...s,
        scenario_id: scenarioIdMap[Number(s.scenario_id)] ?? Number(s.scenario_id),
        category,
      }));

      // Pin created scenario ids
      const createdPinned = Object.entries(scenarioIdMap)
        .map(([k, v]) => ({ from: Number(k), to: Number(v) }))
        .filter(({ from, to }) => from !== 0 && to !== 0)
        .map(({ to }) => to);

      if (createdPinned.length > 0) {
        const byScenarioId = results.reduce((acc, r) => {
          const sid = Number((r as any).scenario_id);
          if (!acc[sid]) acc[sid] = [];
          acc[sid].push(r);
          return acc;
        }, {} as Record<number, any[]>);

        createdPinned.forEach((sid) => {
          const existing = summary.find((s) => Number((s as any).scenario_id) === sid);
          if (!existing) {
            const rows: ScenarioResult[] = (byScenarioId[sid] ?? []) as ScenarioResult[];
            const totalScenarioNcr = rows.reduce((sum: number, r: ScenarioResult) => sum + Number(r.total_estimated_ncr || 0), 0);
            const uniquePf = new Set(rows.map((r: ScenarioResult) => String(r.product_family_id)));
            summary = [
              {
                scenario_id: sid,
                logged_in_user: state.currentUser,
                category,
                total_scenario_ncr: totalScenarioNcr,
                product_count: uniquePf.size,
                created_at: new Date().toISOString(),
              } as any,
              ...summary,
            ];
          }
        });

        setPinnedAlternateScenarioIds((prev) => {
          const next = [...prev, ...createdPinned.filter(id => !prev.includes(id))];
          return next;
        });

        // Track which parent scenario each alternate was derived from
        const parentMap: Record<number, number> = {};
        createdPinned.forEach((sid) => {
          parentMap[sid] = legacyAlternateScenario.scenario_id;
        });
        dispatch(scenarioActions.mergeAlternateParentMap(parentMap));
      }

      if (Array.isArray(results) && results.length > 0) {
        dispatch(scenarioActions.appendScenarioResults(results as any, summary as any));
        // Merge rebate drill-down rows for the alternate, remapping scenario ids the same way.
        // Without this the renumbered scenario has no rows under its new id, so the product
        // family label renders unlinked and the export's rebate sheet skips it.
        const respRebateDetails = (resp as any)?.rebate_details as RebateDetail[] | undefined | null;
        if (respRebateDetails && respRebateDetails.length > 0) {
          dispatch(scenarioActions.mergeRebateDetails(
            respRebateDetails.map(d => ({
              ...d,
              scenario_id: scenarioIdMap[Number(d.scenario_id)] ?? Number(d.scenario_id),
            }))
          ));
        }
        toast.current?.show({
          severity: 'success',
          summary: 'Calculated',
          detail: 'Alternate scenario calculated and added to results.',
          life: 3000,
        });
        closeLegacyAlternateModal();
      } else {
        toast.current?.show({
          severity: 'warn',
          summary: 'No results',
          detail: 'The calculate endpoint returned no results.',
          life: 3500,
        });
      }
    } catch (e) {
      toast.current?.show({
        severity: 'error',
        summary: 'Calculate failed',
        detail: handleApiError(e),
        life: 4500,
      });
    } finally {
      setIsCalculatingLegacyAlternate(false);
    }
  };

  // Share bar plus percentage, used by both alternate views.
  const renderAltShare = (fraction: number, fillColor: string = 'var(--siq-primary)') => {
    const safe = Number.isFinite(fraction) ? Math.max(0, Math.min(1, fraction)) : 0;
    return (
      <span className="inline-flex align-items-center u-gap-2">
        <ProgressBar value={safe * 100} color={fillColor} className="w-5rem flex-none" aria-hidden="true" />
        {formatPercentage(safe)}
      </span>
    );
  };

  const renderLegacyAlternateContent = () => {
    if (!legacyAlternateScenario) return null;

    const details = legacyAlternateScenario.details;
    const categoryKey = selectedCategory || details?.[0]?.category;
    const allFamiliesForCategory = categoryKey ? (productFamiliesByCategory[categoryKey] ?? []) : [];

    // Build rows: prefer category-level product families, fallback to scenario details
    type LegacySliderRow = { productFamilyId: string; productFamilyName: string; currentDistribution: number };
    let sliderRows: LegacySliderRow[];

    if (allFamiliesForCategory.length > 0) {
      sliderRows = allFamiliesForCategory
        .filter((pf) => pf.product_family_id !== null && pf.product_family_id !== undefined)
        .map((pf) => {
          const pfId = String(pf.product_family_id);
          const detail = details.find((d) => String(d.product_family_id) === pfId);
          return {
            productFamilyId: pfId,
            productFamilyName: String(pf.product_family_name ?? ''),
            currentDistribution: Number(detail?.future_distribution ?? 0),
          };
        });
    } else {
      sliderRows = details.map((d) => ({
        productFamilyId: String(d.product_family_id),
        productFamilyName: String(d.product_family_name ?? ''),
        currentDistribution: Number(d.future_distribution ?? 0),
      }));
    }

    sliderRows = sortByFamily(sliderRows, (r) => r.productFamilyName);

    // Compute max market share per product family from payer preferences (same formula as Generator)
    // maxShare = 100% minus the sum of payer percentages for unchecked (ineligible) payers
    const maxShareByPfId: Record<string, number> = {};
    if (payerMix.length > 0) {
      sliderRows.forEach((row) => {
        const pfId = row.productFamilyId;
        const eligibilityRow = drugPayerMatrix[pfId] || {};
        const uncheckedPercent = payerMix.reduce((sum, item) => {
          const isChecked = eligibilityRow[item.payer] ?? true; // default eligible if not set
          return !isChecked ? sum + item.percent : sum;
        }, 0);
        maxShareByPfId[pfId] = Math.round(Math.max(0, 100 - uncheckedPercent) * 10) / 10;
      });
    }
    const hasPayerData = payerMix.length > 0 && Object.keys(drugPayerMatrix).length > 0;

    const baseLabel = getScenarioDisplayLabel(legacyAlternateScenario.scenario_id);
    const totalPct = Math.round(legacyAlternateDistributionTotal * 100);
    const currentTotalPct = Math.round(sliderRows.reduce((s, r) => s + r.currentDistribution, 0) * 100);

    return (
      <AlternateMixTable
        rows={sliderRows}
        baseLabel={baseLabel}
        values={legacyAlternateDistributions}
        onChange={(pfId, fraction) => setLegacyAlternateDistributions((prev) => ({ ...prev, [pfId]: fraction }))}
        maxShare={hasPayerData ? maxShareByPfId : {}}
        labelFor={formatPfLabel}
        colorFor={(name) => (name ? getColorForFamily(name) : 'var(--siq-icon-faint)')}
        renderShare={renderAltShare}
        currentTotalPct={currentTotalPct}
        totalPct={totalPct}
      />
    );
  };

  // `part` picks the dialog body or its footer: both depend on the same per-scenario state computed here.
  const renderDecisionLogicContent = (rowData: GroupedScenarioResult, part: 'body' | 'footer' = 'body') => {
    const details = rowData.details;

    const categoryKey = selectedCategory || details?.[0]?.category;
    const allFamiliesForCategory = categoryKey ? (productFamiliesByCategory[categoryKey] ?? []) : [];

    // Payers that were fully deselected at generation time (every originally-selected
    // product family unchecked). They stay excluded for every alternate of this
    // scenario — regardless of which product families the user later assigns — so
    // their dropped volume never re-enters the category.
    //
    // Two sources, unioned so this survives page reloads / opening from Favorites:
    //   1. state.excludedPayerInfo — populated during generation in this session.
    //   2. Derived from the current category drug-payer matrix over the ORIGINAL
    //      generation's product families — the same rule the backend applies, so it
    //      still works after session state has been cleared.
    //
    // The exclusion basis is the ORIGINAL (non-alternate) scenarios' product families
    // THAT ACTUALLY CARRY VOLUME, NOT this scenario's own details. Two reasons:
    //   - Alternates are generated over the full category family list, so their details
    //     carry a broader PF set; using rowData.details would widen the basis and make a
    //     payer merely allowed on a never-selected family look "not excluded" (this is
    //     why an alternate-of-an-alternate lost the exclusion).
    //   - Original scenarios still contain zero-distribution rows for families that were
    //     never selected (shown at 0%). A payer that's allowed on such a 0-volume family
    //     would fail the "deselected on every family" test and lose its exclusion. The
    //     backend only checks families with min > 0, so we mirror that by requiring a
    //     non-zero distribution here.
    const originalSelectedPfIds = (() => {
      const ids = new Set<string>();
      state.scenarioResults.forEach((r) => {
        if (categoryKey && r.category !== categoryKey) return;
        if (pinnedAlternateScenarioIds.includes(Number(r.scenario_id))) return; // skip alternates
        if (Number(r.future_distribution ?? 0) <= 0) return; // skip 0-volume / never-selected families
        const id = String(r.product_family_id ?? '');
        if (id && id !== 'undefined' && id !== 'null') ids.add(id);
      });
      return ids;
    })();

    const scenarioProductFamilyIds = (originalSelectedPfIds.size > 0
      ? Array.from(originalSelectedPfIds)
      : details.map((d) => String(d.product_family_id))
    ).filter((id) => id !== '' && id !== 'undefined' && id !== 'null');

    const sessionExcludedPayers = (categoryKey
      ? state.excludedPayerInfo[categoryKey]?.excludedPayers ?? []
      : []
    ).map((p) => normalizePayerName(String(p)));

    const derivedExcludedPayers = (payerMix || [])
      .filter((p) => p && p.payer)
      .filter((p) => {
        if (scenarioProductFamilyIds.length === 0) return false;
        const norm = normalizePayerName(String(p.payer));
        // Excluded only if EXPLICITLY deselected on every one of this scenario's
        // product families. Missing/unknown entries default to eligible (not excluded),
        // so a missing matrix can never produce a false exclusion.
        return scenarioProductFamilyIds.every(
          (pfId) => (drugPayerMatrixByNormalizedPayer[pfId]?.[norm] ?? true) === false
        );
      })
      .map((p) => normalizePayerName(String(p.payer)));

    const excludedPayerNormalizedSet = new Set([
      ...sessionExcludedPayers,
      ...derivedExcludedPayers,
    ]);

    // Build an ID -> Product Family Name lookup from the category list (preferred), with scenario details as fallback.
    const productFamilyNameById = allFamiliesForCategory
      .reduce((acc, pf) => {
        const id = pf.product_family_id;
        const name = pf.product_family_name;
        if (id !== null && id !== undefined && name) {
          acc[String(id)] = String(name);
        }
        return acc;
      }, {} as Record<string, string>);

    // For the payer eligibility breakdown, always show all PFs from the category (when available).
    // Fallback to PFs present in the scenario details if the category list hasn't loaded.
    const payerBreakdownProductFamilies: Array<{ productFamilyId: string; productFamilyName: string }> =
      allFamiliesForCategory.length > 0
        ? allFamiliesForCategory
            .filter((pf) => pf.product_family_id !== null && pf.product_family_id !== undefined)
            .map((pf) => ({
              productFamilyId: String(pf.product_family_id),
              productFamilyName: String(pf.product_family_name ?? ''),
            }))
        : details
            .reduce((acc, d) => {
              const id = String(d.product_family_id);
              if (!acc.some((x) => x.productFamilyId === id)) {
                acc.push({
                  productFamilyId: id,
                  productFamilyName: String(d.product_family_name || ''),
                });
              }
              return acc;
            }, [] as Array<{ productFamilyId: string; productFamilyName: string }>);

    details.forEach((row) => {
      const id = row.product_family_id;
      const name = row.product_family_name;
      if (id !== null && id !== undefined && name) {
        productFamilyNameById[String(id)] = String(name);
      }
    });

    const payerRows = payerAssignmentsToRows(rowData.payerAssignments, productFamilyNameById, payerMixByName);
    const totalAllocatedPercent = payerRows.reduce((sum, r) => {
      const pct = typeof r.payerPercent === 'number' && !Number.isNaN(r.payerPercent) ? r.payerPercent : 0;
      return sum + pct;
    }, 0);
    const hasAnyAllocatedPercent = payerRows.some((r) => typeof r.payerPercent === 'number' && !Number.isNaN(r.payerPercent));

    // Payers in the mix with no assignment, which is why the total can be under 100%: excluded (no product
    // family allows them) or too small for the generator to assign individually.
    const assignedPayers = new Set(payerRows.map((r) => normalizePayerName(String(r.payer))));
    const unassignedPayers = (payerMix || [])
      .filter((p) => p && p.payer && Number(p.percent) > 0 && !assignedPayers.has(normalizePayerName(String(p.payer))))
      .map((p) => ({ payer: String(p.payer), percent: Number(p.percent) }));
    const excludedUnassigned = unassignedPayers.filter((p) => excludedPayerNormalizedSet.has(normalizePayerName(p.payer)));
    const tooSmallUnassigned = unassignedPayers.filter((p) => !excludedPayerNormalizedSet.has(normalizePayerName(p.payer)));

    // Detect product families whose final distribution was lifted above the payer volume
    // actually assigned to them (a minimum top-up). The extra volume is drawn from families
    // above their minimums, so the payer assignments legitimately total less than the
    // results-table percentage. The requested minimum (if known) is resolved by a
    // normalized name match so the message can show the true target the user set.
    const pctFmt = (n: number) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(n);
    const selectedFamilies = state.generatorState?.selectedFamilies ?? {};
    const requestedMinPctByName: Record<string, number> = {};
    Object.keys(selectedFamilies).forEach((k) => {
      requestedMinPctByName[k.trim().toLowerCase()] = Number(selectedFamilies[k]?.minDistribution ?? 0) * 100;
    });
    const assignedPercentByPfId: Record<string, number> = {};
    payerRows.forEach((r) => {
      const pct = typeof r.payerPercent === 'number' && !Number.isNaN(r.payerPercent) ? r.payerPercent : 0;
      const id = String(r.productFamilyId);
      assignedPercentByPfId[id] = (assignedPercentByPfId[id] ?? 0) + pct;
    });
    const raisedToFloorFamilies = details.reduce((acc, d) => {
      const id = String(d.product_family_id);
      if (acc.some((x) => x.id === id)) return acc;
      const finalPct = Number(d.future_distribution ?? 0) * 100;
      const assignedPct = assignedPercentByPfId[id] ?? 0;
      if (finalPct - assignedPct < 0.1) return acc;
      const name = productFamilyNameById[id] ?? String(d.product_family_name ?? id);
      const requestedMinPct = requestedMinPctByName[name.trim().toLowerCase()] ?? 0;
      // Only surface the "minimum distribution applied" note when a minimum was
      // actually set for this family. Otherwise pure rounding/redistribution gaps
      // between the assigned payer volume (e.g. 44.7%) and the final distribution
      // (e.g. 45%) would trigger a misleading "lifted to meet its minimum" message
      // even when no minimum was configured.
      if (requestedMinPct <= 0) return acc;
      acc.push({ id, name, assignedPct, finalPct, requestedMinPct });
      return acc;
    }, [] as Array<{ id: string; name: string; assignedPct: number; finalPct: number; requestedMinPct: number }>);


    // Per-payer profitability for this scenario, keyed by normalized payer name so
    // lookups tolerate casing/spacing. Computed inline with the generation response.
    const profitabilityForScenario = profitabilityByScenario[scenarioKey(categoryKey, rowData.scenario_id)] ?? {};
    const profitabilityByNormalizedPayer: Record<string, PayerProfitabilityItem> = {};
    Object.entries(profitabilityForScenario).forEach(([payer, item]) => {
      profitabilityByNormalizedPayer[normalizePayerName(String(payer))] = item;
    });

    // Which basis each payer's figure rests on. Falls back to the legacy boolean when the
    // response comes from a backend that predates `basis`.
    const profitabilityBasis = (item: PayerProfitabilityItem) =>
      item.basis ?? (item.fee_schedule_missing ? 'medicare_estimate' : 'fee_schedule');

    // Product families in this scenario whose payers have no fee schedule at all, so their
    // profitability is suppressed. Drives the notice under the table.
    const familiesWithoutFeeSchedule = payerRows.reduce((acc, r) => {
      const item = profitabilityByNormalizedPayer[normalizePayerName(String(r.payer))];
      if (!item || profitabilityBasis(item) !== 'unavailable') return acc;
      const name = (r.productFamilyName && r.productFamilyName !== r.productFamilyId)
        ? formatPfLabel(String(r.productFamilyName))
        : '';
      if (name && !acc.includes(name)) acc.push(name);
      return acc;
    }, [] as string[]);

    const renderProfitabilityCell = (payer: string) => {
      const item = profitabilityByNormalizedPayer[normalizePayerName(payer)];
      if (!item) {
        return <span className="siq-dim text-tertiary">—</span>;
      }
      const warnClass = `profit-fee-warn-${normalizePayerName(payer).replace(/[^a-z0-9]/g, '')}`;

      // No payer on this product family has an entered rate, so every payer would be priced at
      // the same Medicare rate and the figure would just re-state the payer mix. Show that it
      // can't be calculated instead of a number that implies payer-specific precision.
      if (profitabilityBasis(item) === 'unavailable' || item.profit == null) {
        return (
          <span className="pa-profit inline-flex align-items-center justify-content-end u-gap-2 w-full">
            <Tooltip target={`.${warnClass}`} position="top" />
            <i
              className={`pi pi-info-circle text-tertiary text-xs ${warnClass}`}
              data-pr-tooltip="No fee schedule entered for this product family, so payer profitability can't be calculated. Add reimbursement rates to see it."
            />
            <span className="siq-dim text-tertiary">—</span>
          </span>
        );
      }

      const isGreen = item.indicator === 'green';
      const isRed = item.indicator === 'red';
      // Polarity only: profitable in green, unprofitable in red, break-even plain.
      const toneClass = isGreen ? 'siq-pos text-success' : isRed ? 'siq-neg text-danger' : '';
      const ariaLabel = `${isGreen ? 'Profitable' : isRed ? 'Unprofitable' : 'Break-even'}: ${money(item.profit)}`;
      return (
        <span className="pa-profit inline-flex align-items-center justify-content-end u-gap-2 w-full" aria-label={ariaLabel}>
          {item.fee_schedule_missing && (
            <>
              <Tooltip target={`.${warnClass}`} position="top" />
              <i
                className={`pi pi-exclamation-triangle siq-warn-icon text-warning text-xs ${warnClass}`}
                data-pr-tooltip="No fee schedule rate entered for this payer — reimbursement uses the CMS Medicare rate, so this figure is an estimate."
              />
            </>
          )}
          <span className={`pa-profit-value font-semibold ${toneClass}`}>{signedMoney(item.profit)}</span>
        </span>
      );
    };
    const expandedForScenario = expandedDecisionPayers[rowData.scenario_id] ?? {};

    const isAlternateForThisScenario = alternateModeEnabled && alternateScenarioId === rowData.scenario_id;

    const hasAlternateAssignmentChanges = (() => {
      if (!isAlternateForThisScenario) return false;

      const base = rowData.payerAssignments ?? {};
      const baseByNormalized: Record<string, string> = {};
      Object.entries(base).forEach(([payer, pfId]) => {
        baseByNormalized[normalizePayerName(String(payer))] = String(pfId);
      });

      const altByNormalized: Record<string, string> = {};
      Object.entries(alternatePayerAssignments ?? {}).forEach(([payer, pfId]) => {
        altByNormalized[normalizePayerName(String(payer))] = String(pfId);
      });

      const keys = Object.keys(baseByNormalized).concat(Object.keys(altByNormalized));
      const seen: Record<string, true> = {};
      for (let i = 0; i < keys.length; i += 1) {
        const k = keys[i];
        if (seen[k]) continue;
        seen[k] = true;
        if ((baseByNormalized[k] ?? '') !== (altByNormalized[k] ?? '')) return true;
      }
      return false;
    })();

    const alternatePayerRows = payerRows.map((r) => {
      const payerKey = String(r.payer);
      return {
        ...r,
        selectedProductFamilyId: String(alternatePayerAssignments[payerKey] ?? r.productFamilyId),
      };
    });

    const productFamilyOptionsAll = Object.entries(productFamilyNameById)
      .map(([id, name]) => ({ label: formatPfLabel(String(name)), value: String(id) }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const currentDistributionByPfId = details.reduce((acc, r) => {
      const id = String(r.product_family_id);
      acc[id] = Number(r.future_distribution ?? 0);
      return acc;
    }, {} as Record<string, number>);

    const candidateProductFamilyIds = Object.keys(productFamilyNameById)
      .map((id) => String(id))
      .filter((id) => id !== '');

    const payerItems = (payerMix || [])
      .filter((p) => p && p.payer)
      .filter((p) => !excludedPayerNormalizedSet.has(normalizePayerName(String(p.payer))))
      .map((p) => ({
        payer: String(p.payer),
        vol: Number(p.percent ?? 0) / 100,
      }));

    const { distributionByProductFamilyId: rawAlternateDistributionByPfId } =
      isAlternateForThisScenario && hasAlternateAssignmentChanges
        ? computeDistributionFromAssignments({
          assignments: alternatePayerAssignments,
          candidateProductFamilyIds,
          payerItems,
          baselineMix: currentDistributionByPfId,
          // No explicit mins available in the Results view; default to 0s.
          minDistributionMap: {},
        })
        : { distributionByProductFamilyId: { ...currentDistributionByPfId } };

    // Match the backend's whole-percent rounding so the preview totals 100% and equals the generated scenario.
    const alternateDistributionByPfId =
      isAlternateForThisScenario && hasAlternateAssignmentChanges
        ? roundDistributionToWholePercents(rawAlternateDistributionByPfId)
        : rawAlternateDistributionByPfId;

    const renderMiniFillBar = (fraction: number, fillColor: string = 'var(--siq-primary)') => renderAltShare(fraction, fillColor);

    const productFamilyPreviewRows = Object.entries(productFamilyNameById)
      .map(([pfId, pfName]) => {
        const current = Number(currentDistributionByPfId[String(pfId)] ?? 0);
        const alt = isAlternateForThisScenario
          ? Number(alternateDistributionByPfId[String(pfId)] ?? 0)
          : current;
        return {
          productFamilyId: String(pfId),
          productFamilyName: String(pfName),
          currentDistribution: current,
          alternateDistribution: alt,
        };
      })
      .sort((a, b) => formatPfLabel(a.productFamilyName).localeCompare(formatPfLabel(b.productFamilyName)));

    const runAlternateCalculation = async () => {
      if (!state.currentUser) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Missing user',
          detail: 'Please select a user before calculating.',
          life: 3000,
        });
        return;
      }

      if (!selectedCategory && !details?.[0]?.category) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Missing therapeutic area',
          detail: 'Please select a therapeutic area before calculating.',
          life: 3000,
        });
        return;
      }

      setIsCalculatingAlternate(true);
      try {
        const category = selectedCategory || details[0].category;
        const productFamilyIds = productFamilyPreviewRows.map((r) => String(r.productFamilyId));
        const fullMatrix = buildFullDrugPayerMatrixForAlternateScenario(
          productFamilyIds,
          payerMix,
          drugPayerMatrix,
          alternatePayerAssignments,
          excludedPayerNormalizedSet
        );

        // Set min_distribution_map to the alternate distribution to collapse to a single possible scenario.
        // Use the unrounded distribution: whole-percent rounding here would quantize each share by up to
        // ~0.5% and shift the SP's derived units, so an out-then-back-in round trip would not return to the
        // original units. The whole-percent version is for display only.
        const minDistMap = productFamilyPreviewRows.reduce<Record<string, number>>((acc, r) => {
          const rawShare = Number(rawAlternateDistributionByPfId[String(r.productFamilyId)] ?? r.alternateDistribution ?? 0) || 0;
          acc[String(r.productFamilyId)] = Math.max(0, Math.min(1, rawShare));
          return acc;
        }, {});

        // Compute max shares like Generator (0..100 scale)
        const computedMaxSharesMap = productFamilyIds.reduce<Record<string, number>>((acc, id) => {
          acc[String(id)] = 100;
          return acc;
        }, {});

        // Build excludedScenarios from cached selectedExcludedOpportunities for this category
        const cachedExcludedOpportunities = state.generatorState?.form?.selectedExcludedOpportunities ?? [];
        const excludedScenarios = cachedExcludedOpportunities
          .filter(opp => opp.category === category)
          .map(opp => ({
            logged_in_user: state.currentUser || '',
            category: opp.category || category,
            product_family_id: Number(opp.product_family_id),
            paid_on: opp.paid_on || '',
            rebate: Number(opp.rebate) ?? 0,
          }));

        const feesMap = buildAlternateProductFamilyFeesMap(productFamilyIds, details);
        const directRebatesMap = buildAlternateProductFamilyDirectRebatesMap(productFamilyIds, details);

        const payload: any = {
          logged_in_user: state.currentUser,
          category,
          max_scenarios: 1,
          // Remove drugs with max share 0 from min_distribution_map (Generator behavior)
          // min_distribution_map: Object.fromEntries(
          //   Object.entries(minDistMap).filter(([drugId]) => (computedMaxSharesMap[drugId] ?? 0) > 0)
          // ),
          min_distribution_map: minDistMap,
          product_family_fees_map: Object.keys(feesMap).length > 0 ? feesMap : undefined,
          product_family_direct_rebates_map: Object.keys(directRebatesMap).length > 0 ? directRebatesMap : undefined,
          drugPayerMatrix: fullMatrix,
          drugMaxShares: computedMaxSharesMap,
          excludedScenarios,
          // Keep alternate scenarios at the same category size the user modeled on generation.
          category_scale_factor: state.generatorState?.form?.category_scale_factor_map?.[category] ?? 1.0,
          batch_or_alternate: 'alternate',
          // Send the user-chosen assignments so the backend computes per-payer profitability
          // for the modeled alternate scenario (returned on resp.profitability).
          payer_assignments: alternatePayerAssignments,
        };

        const resp = await ApiService.generateAndCalculateScenariosWithMinsAndMaxAndPvt(payload);

        const rawResults = (resp as any)?.results ?? [];
        const rawSummary = (resp as any)?.summary ?? [];

        // Scenario 0 is the base/current and replaces the existing 0; everything else gets a new alternate id.
        const scenarioIdMap = allocateAlternateIds((rawResults as any[]).map(r => Number(r.scenario_id)));

        const results = (rawResults as any[]).map((r) => ({
          ...r,
          scenario_id: scenarioIdMap[Number(r.scenario_id)] ?? Number(r.scenario_id),
        }));

        const summaryArray = Array.isArray(rawSummary) ? rawSummary : [rawSummary];
        let summary = (summaryArray as any[]).map((s) => ({
          ...s,
          scenario_id: scenarioIdMap[Number(s.scenario_id)] ?? Number(s.scenario_id),
          category,
        }));

        // Pin any newly created scenario ids (excluding 0) right under 0
        const createdPinned = Object.entries(scenarioIdMap)
          .map(([k, v]) => ({ from: Number(k), to: Number(v) }))
          .filter(({ from, to }) => from !== 0 && to !== 0)
          .map(({ to }) => to);

        // Backend typically won't return payer_assignments; carry it over for the newly created scenario(s).
        if (createdPinned.length > 0) {
          const byScenarioId = results.reduce((acc, r) => {
            const sid = Number((r as any).scenario_id);
            if (!acc[sid]) acc[sid] = [];
            acc[sid].push(r);
            return acc;
          }, {} as Record<number, any[]>);

          createdPinned.forEach((sid) => {
            const existing = summary.find((s) => Number((s as any).scenario_id) === sid);
            if (existing) {
              // Don't overwrite if backend unexpectedly provided something.
              if (!(existing as any).payer_assignments) {
                (existing as any).payer_assignments = { ...alternatePayerAssignments };
              }
              return;
            }

            const rows: ScenarioResult[] = (byScenarioId[sid] ?? []) as ScenarioResult[];
            const totalScenarioNcr = rows.reduce((sum: number, r: ScenarioResult) => sum + Number(r.total_estimated_ncr || 0), 0);
            const uniquePf = new Set(rows.map((r: ScenarioResult) => String(r.product_family_id)));

            summary = [
              {
                scenario_id: sid,
                logged_in_user: state.currentUser,
                category,
                total_scenario_ncr: totalScenarioNcr,
                product_count: uniquePf.size,
                created_at: new Date().toISOString(),
                payer_assignments: { ...alternatePayerAssignments },
              } as any,
              ...summary,
            ];
          });
        }

        if (createdPinned.length > 0) {
          setPinnedAlternateScenarioIds((prev) => {
            const next = [...prev, ...createdPinned.filter(id => !prev.includes(id))];
            return next;
          });

          // Track which parent scenario each alternate was derived from
          const parentMap: Record<number, number> = {};
          createdPinned.forEach((sid) => {
            parentMap[sid] = rowData.scenario_id;
          });
          dispatch(scenarioActions.mergeAlternateParentMap(parentMap));
        }

        if (Array.isArray(results) && results.length > 0) {
          dispatch(scenarioActions.appendScenarioResults(results as any, summary as any));
          // Merge inline profitability, remapping the backend scenario_id to the id we assigned.
          const respProfitability = (resp as any)?.profitability as Record<string, Record<string, PayerProfitabilityItem>> | undefined | null;
          if (respProfitability) {
            const remapped: Record<string, Record<string, PayerProfitabilityItem>> = {};
            Object.entries(respProfitability).forEach(([sid, byPayer]) => {
              const mappedId = scenarioIdMap[Number(sid)] ?? Number(sid);
              remapped[mappedId] = byPayer;
            });
            dispatch(scenarioActions.mergePayerProfitability(category, remapped));
          }
          // Merge rebate drill-down rows for the alternate, remapping scenario ids the same way.
          const respRebateDetails = (resp as any)?.rebate_details as RebateDetail[] | undefined | null;
          if (respRebateDetails && respRebateDetails.length > 0) {
            dispatch(scenarioActions.mergeRebateDetails(
              respRebateDetails.map(d => ({
                ...d,
                scenario_id: scenarioIdMap[Number(d.scenario_id)] ?? Number(d.scenario_id),
              }))
            ));
          }
          toast.current?.show({
            severity: 'success',
            summary: 'Calculated',
            detail: 'Alternate scenario calculated and added to results.',
            life: 3000,
          });

          // Close the Decision Logic modal once results are returned/appended
          closeDecisionLogicModal();
        } else {
          toast.current?.show({
            severity: 'warn',
            summary: 'No results',
            detail: 'The calculate endpoint returned no results.',
            life: 3500,
          });
        }
      } catch (e) {
        toast.current?.show({
          severity: 'error',
          summary: 'Calculate failed',
          detail: handleApiError(e),
          life: 4500,
        });
      } finally {
        setIsCalculatingAlternate(false);
      }
    };

    const toggleExpandedPayer = (payer: string) => {
      setExpandedDecisionPayers((prev) => {
        const current = prev[rowData.scenario_id] ?? {};
        const isOpen = current[payer] === true;
        // Single-expand behavior: either none or only the clicked payer
        const nextForScenario = isOpen ? {} : { [payer]: true };
        return { ...prev, [rowData.scenario_id]: nextForScenario };
      });
    };

    const payerRowExpansionTemplate = (payer: string) => {
      if (payerPrefsLoading) return <div className="pa-expansion flex flex-column u-gap-2 u-pl-10 text-xs text-tertiary">Loading payer preferences…</div>;
      if (payerPrefsError) return <div className="pa-expansion flex flex-column u-gap-2 u-pl-10 text-xs text-tertiary">{payerPrefsError}</div>;

      if (payerBreakdownProductFamilies.length === 0) {
        return <div className="pa-expansion flex flex-column u-gap-2 u-pl-10 text-xs text-tertiary">No product families found for this scenario.</div>;
      }

      return (
        <PayerEligibility
          items={sortByFamily(payerBreakdownProductFamilies, (r) => r.productFamilyName).map((r) => {
            const rawName = String(r.productFamilyName || '');
            return {
              id: String(r.productFamilyId),
              label: rawName ? formatPfLabel(rawName) : '—',
              color: rawName ? getColorForFamily(rawName) : 'var(--siq-icon-faint)',
              allowed: (drugPayerMatrixByNormalizedPayer?.[String(r.productFamilyId)]?.[normalizePayerName(payer)] ?? true) === true,
            };
          })}
        />
      );
    };

    const canRunAlternate = payerMixAvailable && payerRows.length > 0 && productFamilyOptionsAll.length > 0;
    const sid = rowData?.scenario_id;
    const scenarioLabel = sid != null ? getScenarioDisplayLabel(Number(sid)) : `Scenario ${sid}`;

    // Shown only when every payer has a figure: a partial sum would understate the scenario.
    const profitItems = payerRows.map((r) => profitabilityByNormalizedPayer[normalizePayerName(String(r.payer))]);
    const totalProfit = profitItems.length > 0 && profitItems.every((i) => i && profitabilityBasis(i) !== 'unavailable' && i.profit != null)
      ? profitItems.reduce((s, i) => s + Number(i!.profit), 0)
      : null;
    const anyMedicareFallback = profitItems.some((i) => i && i.fee_schedule_missing && profitabilityBasis(i) !== 'unavailable');

    const buildPayerAssignmentRows = () =>
      payerRows.map((pr) => ({
        scenario_id: Number(sid),
        payer: pr.payer,
        product_family_name: pr.productFamilyName ?? '—',
        ...(typeof pr.payerPercent === 'number' ? { payer_percent: pr.payerPercent } : {}),
      }));

    const doExport = (format: 'csv' | 'excel') => {
      toast.current?.show({ severity: 'info', summary: 'Export', detail: `Exporting payer assignments for ${scenarioLabel}`, life: 2000 });
      const scenarioResultRows = rowData.details ?? [];
      void handleExportWithPayerAssignments(
        format,
        scenarioResultRows,
        buildPayerAssignmentRows(),
        rebateDetailsForExport(scenarioResultRows),
        categoryIsOral
      );
    };

    const modalExportItems = [
      { label: 'Export as CSV', icon: 'pi pi-file', command: () => doExport('csv') },
      { label: 'Export as Excel', icon: 'pi pi-file-excel', command: () => doExport('excel') },
    ];

    const payerShare = (r: { payerPercent?: number }) =>
      typeof r.payerPercent === 'number' && !Number.isNaN(r.payerPercent)
        ? `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(r.payerPercent)}%`
        : '—';

    const familyCell = (r: { productFamilyName?: string; productFamilyId: string }) => {
      const raw = (r.productFamilyName && r.productFamilyName !== r.productFamilyId) ? String(r.productFamilyName) : '';
      if (!raw) return <span>—</span>;
      return <span className="pa-family inline-flex align-items-center u-gap-2 min-w-0"><i className="siq-swatch" style={{ background: getColorForFamily(raw) }} />{formatPfLabel(raw)}</span>;
    };

    if (part === 'footer') {
      if (!isAlternateForThisScenario || !canRunAlternate) {
        return <Button variant="primary-outline" onClick={closeDecisionLogicModal}>Close</Button>;
      }
      return (
        <>
          <Button variant="primary-outline" onClick={() => {
              setAlternateModeEnabled(false);
              setAlternatePayerAssignments({ ...(rowData.payerAssignments ?? {}) });
            }} disabled={isCalculatingAlternate}>Cancel</Button>
          <Button onClick={runAlternateCalculation} disabled={isCalculatingAlternate || !hasAlternateAssignmentChanges}><i className="pi pi-calculator" aria-hidden="true" />{isCalculatingAlternate ? 'Calculating…' : 'Calculate'}</Button>
        </>
      );
    }

    return (
      <div className="pa-modal flex flex-column u-gap-4">
        {/* Summary and actions; edit mode has none here (Cancel and Calculate sit in the footer). */}
        {!isAlternateForThisScenario && (
          <div className="flex justify-content-between align-items-center u-gap-3 flex-wrap">
            <div className="flex align-items-baseline u-gap-2 flex-wrap text-xs text-tertiary">
              {totalProfit !== null && (
                <>
                  <span>Total profitability</span>
                  <span className={`pa-total-value text-lg font-semibold text-default${totalProfit < 0 ? ' siq-neg text-danger' : ''}`}>{signedMoney(totalProfit)}</span>
                  <span>across {payerRows.length} payers</span>
                </>
              )}
            </div>
            <div className="flex align-items-center u-gap-2 flex-wrap u-ml-auto">
              {payerRows.length > 0 && (
                <>
                  <Menu model={modalExportItems} popup ref={modalExportMenuRef} />
                  <Button variant="primary-outline" onClick={(e) => modalExportMenuRef.current?.toggle(e)} aria-haspopup="menu"><i className="pi pi-download" aria-hidden="true" />Export<i className="pi pi-angle-down" aria-hidden="true" /></Button>
                </>
              )}
              {canRunAlternate && (
                <Button variant="primary-outline" onClick={() => {
                    setAlternateScenarioId(rowData.scenario_id);
                    setAlternatePayerAssignments({ ...(rowData.payerAssignments ?? {}) });
                    setAlternateModeEnabled(true);
                  }}><i className="pi pi-play" aria-hidden="true" />Run alternate scenario</Button>
              )}
            </div>
          </div>
        )}

        {isAlternateForThisScenario && (
          <div className="flex flex-column u-gap-2">
            <Button
              variant="secondary-text"
              className="align-self-start"
              aria-expanded={alternatePreviewExpanded}
              onClick={() => setAlternatePreviewExpanded((prev) => !prev)}
            >
              <i className={`pi ${alternatePreviewExpanded ? 'pi-chevron-down' : 'pi-chevron-right'}`} aria-hidden="true" />
              Compare {scenarioLabel} with this alternate
            </Button>
            {alternatePreviewExpanded && (
              <AlternatePreviewTable
                rows={productFamilyPreviewRows}
                scenarioLabel={scenarioLabel}
                renderFamily={(name) => <span className="pa-family inline-flex align-items-center u-gap-2 min-w-0"><i className="siq-swatch" style={{ background: name ? getColorForFamily(name) : 'var(--siq-icon-faint)' }} />{formatPfLabel(name)}</span>}
                renderShare={(fraction) => renderMiniFillBar(Number(fraction ?? 0))}
              />
            )}
          </div>
        )}

        {payerRows.length === 0 ? (
          <span className="siq-dim text-tertiary">—</span>
        ) : !isAlternateForThisScenario ? (
          <>
            <PayerAssignmentTable
              rows={payerRows}
              expandedPayer={Object.keys(expandedForScenario).find((payer) => expandedForScenario[payer]) ?? null}
              onToggle={toggleExpandedPayer}
              renderExpansion={payerRowExpansionTemplate}
              renderFamily={familyCell}
              renderProfit={renderProfitabilityCell}
              totalShare={hasAnyAllocatedPercent ? new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(totalAllocatedPercent) + '%' : '—'}
              tooSmall={tooSmallUnassigned}
              excluded={excludedUnassigned}
            />
            {anyMedicareFallback && (
              <span className="inline-flex align-items-center u-gap-2 text-xs text-tertiary">
                <i className="pi pi-exclamation-triangle siq-warn-icon text-warning text-xs" aria-hidden="true" />
                Uses the Medicare fallback rate (no custom fee schedule)
              </span>
            )}
            {familiesWithoutFeeSchedule.length > 0 && (
              <Note tone="warning" title="Payer profitability unavailable.">
                No fee schedule has been entered for {familiesWithoutFeeSchedule.join(', ')}, so
                every payer on {familiesWithoutFeeSchedule.length > 1 ? 'those families' : 'that family'} would
                be priced at the same Medicare rate — the result would only re-state the payer
                mix, not tell you which payer is profitable. Enter reimbursement rates to see it.
              </Note>
            )}
            {raisedToFloorFamilies.length > 0 && (
              <Note tone="info" title="Minimum distribution applied.">
                {raisedToFloorFamilies.map((f, i) => {
                  const punct = i < raisedToFloorFamilies.length - 1 ? ';' : '.';
                  return (
                    <span key={f.id}>
                      {i > 0 ? ' ' : ''}
                      {f.requestedMinPct > 0 ? (
                        <>
                          {formatPfLabel(f.name)} was held to its {pctFmt(f.requestedMinPct)}% minimum
                        </>
                      ) : (
                        <>
                          {formatPfLabel(f.name)} was lifted to {pctFmt(f.finalPct)}% to meet its minimum
                          {` (assigned payers total ${pctFmt(f.assignedPct)}%)`}
                        </>
                      )}
                      {punct}
                    </span>
                  );
                })}{' '}
                The extra volume is drawn from families above their minimums, so the payer row totals
                above might not match the distribution shown in the results table.
              </Note>
            )}
          </>
        ) : (
          <>
            <AlternateAssignmentTable
              rows={alternatePayerRows.map((r) => ({ payer: String(r.payer), share: payerShare(r), selectedProductFamilyId: String(r.selectedProductFamilyId ?? '') }))}
              // Only the families this payer is allowed on; with no matrix entry, allowed.
              optionsFor={(payer) => productFamilyOptionsAll.filter((opt) => (drugPayerMatrix?.[String(opt.value)]?.[payer] ?? true) === true)}
              onAssign={(payer, next) => setAlternatePayerAssignments((prev) => ({ ...prev, [payer]: next }))}
              disabled={isCalculatingAlternate || productFamiliesLoading}
            />

            {productFamiliesError && (
              <TextError>{productFamiliesError}</TextError>
            )}
          </>
        )}
      </div>
    );
  };


  // Expanded row: the detailed breakdown plus the payer-assignment and alternate actions.
  const renderRowExpansion = (rowData: GroupedScenarioResult) => {
    const details = rowData.details;
    const hasPayerAssignments = !!rowData.payerAssignments && Object.keys(rowData.payerAssignments).length > 0;

    // Build the set of PF IDs missing a fee schedule for this row's category (used in weighted mode only)
    const category = selectedCategory || details[0]?.category;
    const missingPfIdSet = new Set<string>(
      (state.missingFeeSchedulePfIds?.[category ?? ''] ?? []).map(String)
    );
    // Per-PF partial gaps: PF has a schedule but is missing rates for some eligible payers.
    const feeGaps = state.feeScheduleGaps?.[category ?? ''] ?? {};

    const renderFamily = (row: ScenarioResult, label: string) => {
      const isMissing = ncrMode === 'weighted' && missingPfIdSet.has(String(row.product_family_id));
      const tooltipId = `fee-sched-tip-${row.scenario_id}-${row.product_family_id}`;
      // Partial gap only when the PF isn't wholly missing (whole-PF warning takes priority).
      const gap = (ncrMode === 'weighted' && !isMissing)
        ? feeGaps[String(row.product_family_id)]
        : undefined;
      const gapTooltipId = `fee-gap-tip-${row.scenario_id}-${row.product_family_id}`;
      const gapTooltip = gap
        ? `Missing fee-schedule rate${gap.payers.length > 1 ? 's' : ''} for ${gap.payers
            .map(p => `${p.payer} (${p.pct.toFixed(1)}%)`)
            .join(', ')} — ~${gap.fallback_pct.toFixed(1)}% of this product family's volume uses Medicare, so weighted NCR is approximate.`
        : '';
      const openRebateDetail = () => {
        setRebateDetailTarget({
          scenarioId: Number(row.scenario_id),
          pfId: String(row.product_family_id),
          pfLabel: label,
        });
        setRebateDetailVisible(true);
      };
      // Only linked when there are rows, so the dialog cannot open onto nothing.
      // "No contracts" vs "not loaded" are different claims — the latter is not ours
      // to make when we simply have no rows yet.
      const isEarning = pfRebateEarning.get(`${Number(row.scenario_id)}:${String(row.product_family_id)}`);
      // A direct rebate is a column on this row rather than a rebate row, so it can be
      // the only thing the drill-down has to show -- the link has to open for it too.
      const hasDirectRebate = Number(row.total_direct_rebate ?? 0) > 0;
      const hasRebates = isEarning !== undefined || hasDirectRebate;
      const rebateTip = hasRebates
        ? (isEarning || hasDirectRebate
            ? 'View projected rebate performance'
            : 'View potential rebate performance')
        : rebateDetails.length > 0
          ? 'No rebate contracts exist for this product family'
          : "Rebate detail isn't loaded for this session — run a new generation to view it";
      // the app's Tooltip, not a native `title`, so it gets the design-system tooltip styling.
      const rebateTipId = `rebate-tip-${row.scenario_id}-${row.product_family_id}`;
      return (
        <span className="results-family-cell inline-flex align-items-center u-gap-1 min-w-0 flex-wrap">
          {hasRebates ? (
            <Button variant="link" className="text-xs" id={rebateTipId} data-pr-tooltip={rebateTip} data-pr-position="top" onClick={openRebateDetail}>
              {label}
            </Button>
          ) : (
            <span id={rebateTipId} data-pr-tooltip={rebateTip} data-pr-position="top">{label}</span>
          )}
          {showStrength && strengthOf(row.category, row.product_family_id, row.product_family_name) && (
            <span className="siq-family-strength text-tertiary text-2xs font-medium white-space-nowrap">{strengthOf(row.category, row.product_family_id, row.product_family_name)}</span>
          )}
          <Tooltip position="top" target={`#${rebateTipId}`} />
          {isMissing && (
            <>
              <i
                id={tooltipId}
                className="pi pi-exclamation-triangle siq-warn-icon text-warning text-xs"
                data-pr-tooltip="No fee schedule found — weighted NCR is using Medicare rates for this product family."
                data-pr-position="top"
              />
              <Tooltip position="top" target={`#${tooltipId}`} />
            </>
          )}
          {gap && (
            <>
              <i
                id={gapTooltipId}
                className="pi pi-exclamation-circle siq-warn-icon text-warning text-xs"
                data-pr-tooltip={gapTooltip}
                data-pr-position="top"
              />
              <Tooltip position="top" target={`#${gapTooltipId}`} />
            </>
          )}
        </span>
      );
    };

    return (
      <div className="results-breakdown flex flex-column u-gap-2">
        <div className="results-breakdown-head flex justify-content-between align-items-center u-gap-3 flex-wrap">
          <span className="results-breakdown-title text-xs font-semibold">Detailed breakdown</span>
          <div className="results-breakdown-actions flex u-gap-2 flex-wrap">
            {hasPayerAssignments && (
              <Button variant="primary-outline" onClick={() => openDecisionLogicModal(rowData)}><i className="pi pi-eye" aria-hidden="true" />View payer assignments</Button>
            )}
            {hasPayerAssignments ? (
              <Button variant="primary-outline" onClick={() => openDecisionLogicModal(rowData, { startAlternate: true })} disabled={!payerMixAvailable}><i className="pi pi-play" aria-hidden="true" />Run alternate scenario</Button>
            ) : (
              <Button variant="primary-outline" onClick={() => openLegacyAlternateModal(rowData)}><i className="pi pi-play" aria-hidden="true" />Run alternate scenario</Button>
            )}
          </div>
        </div>
        <BreakdownTable rows={details} metric={ncrMode} unitMode={unitMode} ncrLabel={ncrLabel} renderFamily={renderFamily} familyNote={showStrength ? 'Invoice unit strength' : undefined} />
      </div>
    );
  };

  const getFilteredResults = (): GroupedScenarioResult[] => {
    const filtered = orderedScenarioResults;

    const grouped = filtered.reduce((acc, row) => {
      if (!acc[row.scenario_id]) acc[row.scenario_id] = [];
      acc[row.scenario_id].push(row);
      return acc;
    }, {} as Record<number, ScenarioResult[]>);

    const list = Object.entries(grouped).map(([scenarioId, rows]) => {
      const sid = Number(scenarioId);
      // Find the summary matching this scenario and the selected category (to get correct payer_assignments).
      // Fall back to any summary for this scenario_id if no category is selected or no category-specific match.
      const categorySummary = selectedCategory
        ? state.scenarioSummary.find(s => s.scenario_id === sid && s.category === selectedCategory)
        : undefined;
      const summary = categorySummary ?? state.scenarioSummary.find(s => s.scenario_id === sid);
      const parentId = state.alternateParentMap?.[sid];
      return {
        scenario_id: sid,
        summary,
        scenarioKind: (summary as any)?.scenario_kind,
        payerAssignments: (summary as any)?.payer_assignments,
        parentScenarioId: parentId,
        totalNCR: 0,
        totalWeightedNCR: 0,
        totalRebate: 0,
        totalMedicareReimb: 0,
        totalPvtReimb: 0,
        totalPrice: 0,
        distributionBreakdown: [],
        details: rows,
      };
    });

    return [...list].sort((a, b) => sortScenarioIdsWithPinnedAlternates(a.scenario_id, b.scenario_id));
  };

  // One row per scenario for the selected area, with its totals.
  const scenarioGroups: GroupedScenarioResult[] = useMemo(() => getFilteredResults()
    .map(r => {
      const details = selectedCategory ? r.details.filter(d => d.category === selectedCategory) : r.details;
      return {
        ...r,
        details,
        totalNCR: details.reduce((sum, d) => sum + Number(d.total_estimated_ncr || 0), 0),
        totalWeightedNCR: details.reduce((sum, d) => sum + Number(d.total_weighted_ncr || 0), 0),
        totalPrice: details.reduce((sum, d) => sum + (Number(d.net_price || 0)), 0),
        totalRebate: details.reduce((sum, d) => sum + Number(d.total_rebate || 0), 0),
        totalMedicareReimb: details.reduce((sum, d) => sum + Number(d.estimated_medicare_reimbursement || 0), 0),
        totalPvtReimb: details.reduce((sum, d) => sum + Number(d.estimated_pvt_reimbursement || 0), 0),
        distributionBreakdown: getDistributionBreakdown(details),
      };
    })
    .filter(r => r.details.length > 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderedScenarioResults, state.scenarioSummary, state.alternateParentMap, selectedCategory, getColorForFamily]);

  const groupById = useMemo(() => new Map(scenarioGroups.map(g => [g.scenario_id, g])), [scenarioGroups]);

  // Legend: families with volume in any scenario for the area, in the fixed order.
  const familyLegend = useMemo(() => {
    const names = new Map<string, string>();
    scenarioGroups.forEach(g => g.details.forEach(d => {
      if ((d.future_distribution ?? 0) <= 0) return;
      const name = d.product_family_name || 'Other';
      names.set(name.trim().toLowerCase(), name);
    }));
    return sortByFamily(Array.from(names.values()), n => n);
  }, [scenarioGroups]);
  const metricName = ncrMode === 'medicare' ? ncrLabel : 'Weighted';
  const metricNcr = useCallback((g: GroupedScenarioResult) => (ncrMode === 'weighted' ? Number(g.totalWeightedNCR ?? 0) : g.totalNCR), [ncrMode]);
  const metricReimb = (g: GroupedScenarioResult) => (ncrMode === 'weighted' ? g.totalPvtReimb : g.totalMedicareReimb);
  const currentGroup = groupById.get(0);
  const currentNcr = currentGroup ? metricNcr(currentGroup) : null;

  // Pinned above the sorted pages, outside sorting and paging: Current, its scaled reference,
  // Proposed, then user-run alternates (newest first).
  const pinnedIds = useMemo(() => [0, SCALED_CURRENT_SCENARIO_ID, PROPOSED_SCENARIO_ID, ...[...pinnedAlternateScenarioIds].reverse()], [pinnedAlternateScenarioIds]);
  const pinnedRows = useMemo(
    () => pinnedIds.map(id => groupById.get(Number(id))).filter((g): g is GroupedScenarioResult => !!g),
    [pinnedIds, groupById]
  );
  const sortableRows = useMemo(() => {
    const pinned = new Set(pinnedIds.map(Number));
    return scenarioGroups.filter(g => !pinned.has(g.scenario_id));
  }, [scenarioGroups, pinnedIds]);

  const scenarioColumns = useMemo<Record<ScenarioSortKey, SortColumn<GroupedScenarioResult>>>(() => ({
    name: { value: g => getScenarioDisplayLabel(g.scenario_id), kind: 'text', label: 'scenario' },
    reimb: { value: g => (ncrMode === 'weighted' ? g.totalPvtReimb : g.totalMedicareReimb), label: `${metricName} reimb.` },
    price: { value: g => g.totalPrice, label: 'price' },
    rebate: { value: g => g.totalRebate, label: 'rebate' },
    ncr: { value: g => metricNcr(g), label: `${metricName} NCR` },
    delta: { value: g => (currentNcr === null ? null : metricNcr(g) - currentNcr), label: 'change vs current' },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [ncrMode, metricName, metricNcr, currentNcr, altIndexByScenarioId]);

  // "Current and alternates stay pinned": names what's actually pinned above the sorted pages.
  const pinnedNote = (() => {
    const ids = new Set(pinnedRows.map(g => g.scenario_id));
    const alternates = pinnedAlternateScenarioIds.some(id => ids.has(Number(id)));
    const parts = [ids.has(0) || ids.has(SCALED_CURRENT_SCENARIO_ID) ? 'Current' : null, ids.has(PROPOSED_SCENARIO_ID) ? 'Proposed' : null, alternates ? 'alternates' : null].filter((x): x is string => !!x);
    if (!parts.length) return '';
    const list = parts.length === 1 ? parts[0] : `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
    return `${list} ${parts.length === 1 && !alternates ? 'stays' : 'stay'} pinned`;
  })();
  const [scenarioPageSize, setScenarioPageSize] = useState(SCENARIO_PAGE_SIZE);
  const table = useSortedPaged<GroupedScenarioResult, ScenarioSortKey>({
    rows: sortableRows,
    columns: scenarioColumns,
    defaultSort: { key: 'ncr', dir: 'desc' },
    pageSize: scenarioPageSize,
    resetKey: selectedCategory,
  });

  const maxAbsNcr = useMemo(() => scenarioGroups.reduce((m, g) => Math.max(m, Math.abs(metricNcr(g))), 0), [scenarioGroups, metricNcr]);

  // Highest NCR among the scenarios this run generated: not Current, its scaled reference,
  // Proposed, or alternates the user ran afterwards.
  // Across every row in the run: Current, Proposed and alternates count too, so the strip never
  // calls a generated scenario the highest when today's mix or an alternate beats it.
  const highest = useMemo(
    () => scenarioGroups.reduce<GroupedScenarioResult | null>((best, g) => (best === null || metricNcr(g) > metricNcr(best) ? g : best), null),
    [scenarioGroups, metricNcr],
  );
  const highestIsCurrent = highest?.scenario_id === 0;
  const highestDelta = highest && !highestIsCurrent && currentNcr !== null ? metricNcr(highest) - currentNcr : null;
  const animatedDelta = useCountUp(highestDelta ?? 0, `${selectedCategory}|${ncrMode}|${highest?.scenario_id}|${state.generatorState?.lastRun?.generatedAt ?? ''}`);

  const toggleExpanded = (id: number) => setExpandedRows(prev => {
    const next = { ...prev };
    if (next[id]) delete next[id]; else next[id] = true;
    return next;
  });

  const toggleSelected = (id: number) => setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));

  // "View breakdown" on the strip: go to that row's page, expand it and bring it into view.
  const pendingScrollId = useRef<number | null>(null);
  const viewBreakdown = (id: number) => {
    const index = table.sorted.findIndex(g => g.scenario_id === id);
    if (index >= 0) table.setPage(Math.floor(index / scenarioPageSize));
    setExpandedRows(prev => ({ ...prev, [id]: true }));
    pendingScrollId.current = id;
  };
  useEffect(() => {
    const id = pendingScrollId.current;
    if (id === null) return;
    const el = document.getElementById(`scenario-row-${id}`);
    if (!el) return;
    pendingScrollId.current = null;
    // Scroll the page's own scroll area to the row. scrollIntoView would also scroll the app frame
    // (overflow hidden, but still scrollable by script) and push the top bar out of view.
    const row = el.closest('tr') ?? el;
    const work = row.closest('.siq-work');
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    if (work) work.scrollTo({ top: work.scrollTop + row.getBoundingClientRect().top - work.getBoundingClientRect().top, behavior });
    else row.scrollIntoView({ behavior, block: 'start' });
  });

  const openCompare = (ids: number[]) => {
    setCompareIds(ids);
    setComparisonModalVisible(true);
  };
  const compareScenarios = useMemo(() => {
    const ids = [0, ...compareIds.filter(id => id !== 0)];
    return ids.map(id => groupById.get(id)).filter((g): g is GroupedScenarioResult => !!g);
  }, [compareIds, groupById]);

  // Exports: the scenario rows for the area, with payer assignments (by name) and rebate rows.
  const productFamilyNameByIdForExport = useMemo(() => {
    const out: Record<string, string> = {};
    for (const row of selectedCategoryResults) {
      const pfId = String((row as any)?.product_family_id ?? '');
      const pfName = String((row as any)?.product_family_name ?? '');
      if (pfId && pfName && !out[pfId]) out[pfId] = pfName;
    }
    return out;
  }, [selectedCategoryResults]);

  const payerAssignmentsByScenario = useMemo(() => {
    const out: Record<string, Record<string, string> | undefined> = {};
    for (const s of state.scenarioSummary) out[scenarioKey(s.category, s.scenario_id)] = (s as any)?.payer_assignments;
    return out;
  }, [state.scenarioSummary]);

  const runExport = (format: 'csv' | 'excel', scenarioIds: number[] | null, message: string) => {
    const wanted = scenarioIds ? new Set(scenarioIds) : null;
    const rows = wanted ? selectedCategoryResults.filter(r => wanted.has(Number(r.scenario_id))) : selectedCategoryResults;
    const data = rows.map((r) => {
      const assignments = payerAssignmentsByScenario[scenarioKey(r.category, r.scenario_id)];
      const payer_assignments = assignments
        ? Object.fromEntries(Object.entries(assignments).map(([payer, pfId]) => [payer, productFamilyNameByIdForExport[String(pfId)] ?? String(pfId)]))
        : undefined;
      return { ...(r as any), payer_assignments } as ScenarioResult;
    });

    const seen = new Set<number>();
    const payerRows: Array<{ scenario_id: number; payer: string; product_family_name: string; payer_percent?: number }> = [];
    for (const r of data) {
      const sid = Number((r as any)?.scenario_id);
      if (seen.has(sid)) continue;
      seen.add(sid);
      const assignments = payerAssignmentsByScenario[scenarioKey(r.category, sid)];
      if (!assignments) continue;
      for (const [payer, pfIdRaw] of Object.entries(assignments)) {
        const pct = payerMixByName[normalizePayerName(payer)];
        payerRows.push({
          scenario_id: sid,
          payer,
          product_family_name: productFamilyNameByIdForExport[String(pfIdRaw)] ?? String(pfIdRaw),
          ...(typeof pct === 'number' ? { payer_percent: pct } : {}),
        });
      }
    }

    const rebateRows = rebateDetailsForExport(data);
    toast.current?.show({ severity: 'info', summary: 'Export', detail: message, life: 2000 });
    if (payerRows.length > 0) {
      void handleExportWithPayerAssignments(format, data, payerRows, rebateRows, categoryIsOral);
    } else {
      void handleExport(format, data, rebateRows, categoryIsOral);
    }
  };

  // Scoped to the checked scenarios when there are any, as before.
  const exportSelection = selectedIds.length > 0 ? selectedIds : null;
  const exportCount = exportSelection ? exportSelection.length : scenarioGroups.length;
  const exportMessage = exportSelection
    ? `Exporting ${exportCount} scenario${exportCount === 1 ? '' : 's'}`
    : 'Exporting all scenarios';
  // Ally UX Menu items, as on Favorites; the label says which rows go out. "All" also carries the pinned
  // rows, named so the count matches the "N scenarios generated" subtitle: "7 scenarios + current, 1 alternate".
  const exportCountLabel = (() => {
    if (exportSelection) return `${exportCount} selected`;
    const ids = new Set(scenarioGroups.map(g => Number(g.scenario_id)));
    const alternates = pinnedAlternateScenarioIds.filter(id => ids.has(Number(id))).length;
    const pinned = [
      ids.has(0) || ids.has(SCALED_CURRENT_SCENARIO_ID) ? 'current' : null,
      alternates ? `${alternates} alternate${alternates === 1 ? '' : 's'}` : null,
    ].filter(Boolean);
    // Proposed counts as generated, as in the subtitle.
    const generated = scenarioGroups.length - [0, SCALED_CURRENT_SCENARIO_ID].filter(id => ids.has(id)).length - alternates;
    return `${generated} scenario${generated === 1 ? '' : 's'}${pinned.length ? ` + ${pinned.join(', ')}` : ''}`;
  })();
  const exportItems: MenuItem[] = [
    { label: `CSV · ${exportCountLabel}`, icon: 'pi pi-file', command: () => runExport('csv', exportSelection, exportMessage) },
    { label: `Excel · ${exportCountLabel}`, icon: 'pi pi-file-excel', command: () => runExport('excel', exportSelection, exportMessage) },
  ];

  const runLabel = (() => {
    const mode = state.generatorState?.lastRun?.mode
      ?? (state.scenarioSummary.some(s => s.payer_assignments && Object.keys(s.payer_assignments).length > 0) ? 'payer' : 'distribution');
    return mode === 'payer' ? 'Payer optimized' : 'Distribution';
  })();
  const runSubtitle = (() => {
    const areas = Array.from(new Set(state.scenarioResults.map(r => r.category).filter(Boolean)));
    const alternates = new Set(pinnedAlternateScenarioIds.map(Number));
    const generated = new Set(
      state.scenarioResults
        .filter(r => Number(r.scenario_id) !== 0 && Number(r.scenario_id) !== SCALED_CURRENT_SCENARIO_ID && !alternates.has(Number(r.scenario_id)))
        .map(r => `${r.category}:${r.scenario_id}`)
    ).size;
    const stamp = state.generatorState?.lastRun?.generatedAt
      ?? state.scenarioSummary.map(s => s.created_at).filter(Boolean).sort().pop();
    return `${areas.join(', ')} · ${generated} scenario${generated === 1 ? '' : 's'} generated${stamp ? ` ${shortDate(stamp)}` : ''}`;
  })();

  const weightedDisabled = allFeeSchedulesMissing || categoryIsOral;
  const anyModalOpen = isComparisonModalVisible || isDecisionLogicModalVisible || isLegacyAlternateModalVisible || rebateDetailVisible;
  const badgeFor = (id: number) => scenarioBadge(id, PROPOSED_SCENARIO_ID);

  // Nothing generated yet: go straight to New run rather than an empty page that only links there.
  if (state.scenarioResults.length === 0) return <Navigate to="/generate" replace />;

  const excludedInfo = selectedCategory
    ? state.excludedPayerInfo[selectedCategory]
    : Object.values(state.excludedPayerInfo)[0];
  const legacyTotalPct = Math.round(legacyAlternateDistributionTotal * 100);

  // u-pb-16: room at the bottom so the floating compare bar doesn't cover the last rows.
  return (
    <div className={`scenario-results-container flex flex-column u-gap-4 min-w-0${selectedIds.length > 0 ? ' u-pb-16' : ''}`}>
      <PageHeader
        title="Scenario results"
        subtitle={runSubtitle}
        meta={<StatusTag variant="info">{runLabel}</StatusTag>}
        actions={<>
          <Button variant="primary-outline" onClick={() => navigate('/generate')}>Edit run</Button>
          <Menu model={exportItems} popup popupAlignment="right" ref={exportMenuRef} />
          <Button onClick={(e) => exportMenuRef.current?.toggle(e)} disabled={isExporting || selectedCategoryResults.length === 0} aria-haspopup="menu">
            <i className="pi pi-download" aria-hidden="true" />
            Export
            <i className="pi pi-angle-down" aria-hidden="true" />
          </Button>
        </>}
      />

      {/* These selections scope the chart and the scenario table, so they sit above both. */}
      <FilterBand>
        <Field label="Therapeutic area" htmlFor="results-area">
          <Dropdown inputId="results-area" value={selectedCategory} options={categoryOptions} onChange={(e) => setSelectedCategory(e.value)} className="w-11rem" />
        </Field>
        {payerMixAvailable && (
          <Field
            label="Metric"
            htmlFor="results-metric"
            info={weightedDisabled ? (
              <>
                <i
                  id="weighted-ncr-toggle"
                  className="pi pi-info-circle results-info-icon text-tertiary text-xs"
                  data-pr-tooltip={categoryIsOral
                    ? 'Weighted NCR is not applicable for oral inventory categories.'
                    : 'Weighted NCR disabled — no fee schedules for this category.'}
                  data-pr-position="top"
                />
                <Tooltip position="top" target="#weighted-ncr-toggle" />
              </>
            ) : undefined}
          >
            <Dropdown
              inputId="results-metric"
              value={ncrMode}
              options={[
                { label: `${ncrLabel} NCR`, value: 'medicare' },
                { label: 'Weighted NCR', value: 'weighted', disabled: weightedDisabled },
              ]}
              optionDisabled="disabled"
              onChange={(e) => setNcrMode(e.value)}
              className="w-10rem"
            />
          </Field>
        )}
        {payerMixLoaded && (
          <Field label="Units" htmlFor="results-units">
            <Dropdown
              inputId="results-units"
              value={unitMode}
              options={[{ label: 'Category units', value: 'category' }, { label: 'Invoice units', value: 'invoice' }]}
              onChange={(e) => setUnitMode(e.value)}
              className="w-10rem"
            />
          </Field>
        )}
      </FilterBand>

      {highest && (
        <UiCard accent className="results-highest flex align-items-center u-gap-4 flex-wrap u-p-4">
          <div className="flex flex-column u-gap-1 flex-1 min-w-14rem">
            <span className="text-2xs font-bold text-uppercase tracking-wider text-selected">Highest modeled NCR in this run</span>
            <div className="flex align-items-baseline u-gap-3 flex-wrap">
              <span className="results-highest-name text-md font-semibold white-space-nowrap">{getScenarioDisplayLabel(highest.scenario_id)}</span>
              {highestDelta !== null && (
                <span className={`results-highest-delta text-md font-semibold${highestDelta > 0 ? ' siq-pos text-success' : highestDelta < 0 ? ' siq-neg text-danger' : ''}`}>
                  {signedMoney(Math.round(animatedDelta))}
                </span>
              )}
              <span className="results-highest-context text-xs text-tertiary">{highestIsCurrent ? 'No modeled scenario beats today’s mix · ' : highestDelta !== null ? 'vs current · ' : ''}{metricName} NCR {money(metricNcr(highest))}</span>
            </div>
            <div className="siq-legend flex u-gap-3 flex-wrap text-2xs text-secondary">
              {highest.distributionBreakdown.filter(d => d.percent > 0).map(d => (
                <span key={d.family} className="siq-legend-item inline-flex align-items-center u-gap-1 white-space-nowrap"><i className="siq-swatch" style={{ background: d.color }} />{familyLabel(d.family)} {Math.round(d.percent * 100)}%</span>
              ))}
            </div>
          </div>
          <div className="flex u-gap-2 flex-wrap">
            <Tip text={isDemoMode ? 'Read-only demo account' : undefined}>
              <Button variant="primary-outline" onClick={() => toggleFavorite(highest)} disabled={isDemoMode}>
                <i className={isFavorite(highest) ? 'pi pi-star-fill' : 'pi pi-star'} aria-hidden="true" />
                {isFavorite(highest) ? 'Favorited' : 'Favorite'}
              </Button>
            </Tip>
            <Button variant="primary-outline" onClick={() => viewBreakdown(highest.scenario_id)}>View breakdown</Button>
            {!highestIsCurrent && <Button variant="primary-outline" onClick={() => openCompare([highest.scenario_id])}>Compare with current</Button>}
          </div>
        </UiCard>
      )}

      <QtdDistributionChart
        data={selectedCategory ? (productFamiliesByCategory[selectedCategory] ?? []) : []}
        loading={productFamiliesLoading}
      />

      {/* Informational banners between the chart and the scenarios */}
      {state.generationStatus && state.generationStatus.inProgress && !generationBannerDismissed && (
        <Note tone="info" title={`Generating categories: ${state.generationStatus.completedCategories} / ${state.generationStatus.totalCategories} completed.`} onClose={() => setGenerationBannerDismissed(true)}>
          Results auto-append as they finish.
        </Note>
      )}

      {excludedInfo && !excludedPayerBannerDismissed && (
        <Note tone="warning" title={`${(excludedInfo.excludedPayerVolume * 100).toFixed(1)}% of last quarter volume excluded from the generated scenarios (not the baseline or alternate scenarios you run)`} onClose={() => setExcludedPayerBannerDismissed(true)}>
          Payers with all product families deselected: <strong>{excludedInfo.excludedPayers.join(', ')}</strong>
        </Note>
      )}

      <Tooltip position="top" target=".results-info-icon" />
      <UiCard
        title={
          <span className="inline-flex align-items-center u-gap-2">
            Scenarios
            <i
              className="pi pi-info-circle results-info-icon text-tertiary text-xs"
              data-pr-tooltip={ncrMode === 'medicare'
                ? `NCR: (${ncrLabel} Reimbursement + Rebate + Admin Fee) - Price\nPrice: Net Price * Invoice Units\nInvoice Units: Period to date volume, by Account by NDC, unadjusted for Opportunity measures of 'Units'`
                : `Weighted NCR: (Weighted Reimbursement + Rebate + Admin Fee) - Price\nPrice: Net Price * Invoice Units\nInvoice Units: Period to date volume, by Account by NDC, unadjusted for Opportunity measures of 'Units'`}
              data-pr-position="top"
            />
          </span>
        }
        subtitle={<>Expand a row for its product-family breakdown · check scenarios to compare with current<br />{`${table.sortLine} across all pages${pinnedNote ? ` · ${pinnedNote}` : ''}`}</>}
        actions={familyLegend.length > 0 ? (
          <div className="siq-legend flex u-gap-3 flex-wrap text-2xs text-secondary justify-content-end">
            {familyLegend.map(family => (
              <span key={family} className="siq-legend-item inline-flex align-items-center u-gap-1 white-space-nowrap"><i className="siq-swatch" style={{ background: getColorForFamily(family) }} />{familyLabel(family)}</span>
            ))}
          </div>
        ) : selectedCategory ? <span className="text-xs text-secondary">No products for this therapeutic area.</span> : undefined}
      >
        <ScenarioTable
          pinnedRows={pinnedRows}
          pageRows={table.pageRows}
          total={table.total}
          page={table.page}
          pageSize={scenarioPageSize}
          onPagination={(page, size) => {
            if (size !== scenarioPageSize) { setScenarioPageSize(size); table.setPage(0); } else table.setPage(page);
          }}
          sortKey={table.sortKey}
          sortDir={table.sortDir}
          onSort={table.toggleSort}
          expanded={expandedRows}
          onToggleExpanded={toggleExpanded}
          renderExpansion={renderRowExpansion}
          metricName={metricName}
          metricReimb={metricReimb}
          metricNcr={metricNcr}
          currentNcr={currentNcr}
          maxAbsNcr={maxAbsNcr}
          labelOf={getScenarioDisplayLabel}
          badgeFor={badgeFor}
          isFavorite={isFavorite}
          onToggleFavorite={(g) => { void toggleFavorite(g); }}
          isDemoMode={isDemoMode}
          isSelected={(id) => selectedIds.includes(id)}
          onToggleSelected={toggleSelected}
        />
      </UiCard>

      <RebateDetailDialog
        visible={rebateDetailVisible}
        onHide={() => setRebateDetailVisible(false)}
        onExited={() => setRebateDetailTarget(null)}
        target={rebateDetailTarget}
        rebateDetailRows={rebateDetailRows}
        scenarioLabel={rebateDetailTarget ? getScenarioDisplayLabel(rebateDetailTarget.scenarioId) : undefined}
        areaLabel={selectedCategory ?? undefined}
        summaryRow={rebateDetailTarget ? state.scenarioResults.find(r =>
          Number(r.scenario_id) === rebateDetailTarget.scenarioId &&
          String(r.product_family_id) === String(rebateDetailTarget.pfId) &&
          (!selectedCategory || r.category === selectedCategory)
        ) : undefined}
      />

      <Modal
        visible={isDecisionLogicModalVisible}
        onHide={closeDecisionLogicModal}
        onExited={resetDecisionLogicModal}
        title={decisionLogicScenario && alternateModeEnabled && alternateScenarioId === decisionLogicScenario.scenario_id
          ? `Alternate scenario · from ${getScenarioDisplayLabel(decisionLogicScenario.scenario_id)}`
          : `Payer assignments · ${decisionLogicScenario ? getScenarioDisplayLabel(decisionLogicScenario.scenario_id) : ''}`}
        subtitle={decisionLogicScenario && alternateModeEnabled && alternateScenarioId === decisionLogicScenario.scenario_id
          ? 'Reassign payers to other product families, then calculate the NCR impact'
          : 'Payer optimized · each payer is assigned its most profitable product family'}
        width="51.4062rem"
        dismissableMask
        footer={decisionLogicScenario ? renderDecisionLogicContent(decisionLogicScenario, 'footer') : undefined}
      >
        {decisionLogicScenario ? renderDecisionLogicContent(decisionLogicScenario) : null}
      </Modal>

      <Modal
        visible={isLegacyAlternateModalVisible}
        onHide={closeLegacyAlternateModal}
        onExited={resetLegacyAlternateModal}
        title="Alternate scenario"
        subtitle={legacyAlternateScenario ? `Starting from ${getScenarioDisplayLabel(legacyAlternateScenario.scenario_id)} · adjust the mix, then calculate the NCR impact` : undefined}
        width="44.8438rem"
        footerStart={legacyAlternateScenario ? (
          <Button variant="primary-text" onClick={resetLegacyAlternateDistributions} disabled={isCalculatingLegacyAlternate || !hasLegacyDistributionChanges}><i className="pi pi-refresh" aria-hidden="true" />{`Reset to ${getScenarioDisplayLabel(legacyAlternateScenario.scenario_id)}`}</Button>
        ) : undefined}
        footer={<>
          <Button variant="primary-outline" onClick={closeLegacyAlternateModal} disabled={isCalculatingLegacyAlternate}>Cancel</Button>
          <Button onClick={runLegacyAlternateCalculation} disabled={isCalculatingLegacyAlternate || !hasLegacyDistributionChanges || legacyTotalPct !== 100}><i className="pi pi-calculator" aria-hidden="true" />{isCalculatingLegacyAlternate ? 'Calculating…' : 'Calculate'}</Button>
        </>}
      >
        {renderLegacyAlternateContent()}
      </Modal>

      <CompareModal
        visible={isComparisonModalVisible}
        onHide={() => {
          setComparisonModalVisible(false);
          // A comparison is done once it's closed: clear the checked scenarios with it.
          setSelectedIds([]);
        }}
        scenarios={compareScenarios}
        metric={ncrMode}
        unitMode={unitMode}
        ncrLabel={ncrLabel}
        strengthOf={showStrength ? (name: string) => strengthOf(selectedCategory, null, name) : undefined}
        areaLabel={selectedCategory ?? ''}
        getLabel={getScenarioDisplayLabel}
        getBadge={badgeFor}
        getColor={getColorForFamily}
        exporting={isExporting}
        onExport={() => {
          const ids = compareScenarios.map(g => g.scenario_id);
          runExport('excel', ids, `Exporting ${ids.length} scenario${ids.length === 1 ? '' : 's'}`);
        }}
      />

      {selectedIds.length > 0 && !anyModalOpen && (
        <div className="results-compare-bar flex align-items-center u-gap-2 u-py-2 u-pl-4 u-pr-2 bg-base border-1 border-default radius-md shadow-lg text-sm text-nowrap" role="region" aria-label="Comparison">
          <span><b>{selectedIds.length}</b> selected to compare with Current</span>
          <Button variant="primary-text" onClick={() => setSelectedIds([])}>Clear</Button>
          <Button className="results-compare-go" onClick={() => openCompare(selectedIds)}>Compare</Button>
        </div>
      )}

      <div className={`u-pt-4 border-t-1 border-default text-2xs text-tertiary leading-relaxed${selectedIds.length > 0 && !anyModalOpen ? ' results-under-bar' : ''}`}>
        <p className="u-m-0"><b>Ranked by selected criteria; verify before acting.</b> ScenarioIQ rankings are informational scenario comparisons only and are based on available data, selected assumptions, and modeling logic that may be incomplete, outdated, or subject to change. Rankings should not be interpreted as determining, recommending, implying, or directing clinical appropriateness, medical necessity, payer coverage, or which drug should be prescribed, ordered, purchased, dispensed, routed, referred, used, or recommended. Confirm payer requirements, contract terms, formulary status, and patient/practice-specific facts before taking action.</p>
      </div>
    </div>
  );
};

export default ScenarioResults;
